<template>
	<div>
		<v-container v-if="primaryView" class="full-height quote">
		<v-alert
			v-if="$store.getters['ezeepHealth/ezeepHasIssues']"
			:type="$store.getters['ezeepHealth/ezeepStatus'] === 'error' ? 'error' : 'warning'"
			text
			dense
			dismissible
			class="mb-2"
		>
			<strong>Printer Issue:</strong> {{ $store.getters['ezeepHealth/ezeepStatusMessage'] }}.
			Auto-print may not work.
			<router-link v-if="canViewPrinterSettings" to="/settings/printers" class="ml-1">View Printers</router-link>
		</v-alert>
		<v-row v-if="quoteData.quoteId && isLockedQuote" class="flex-none">
			<v-col cols="12" class="flex-none">
				<v-alert type="info" text dense class="quote-locked-banner mb-0" data-test="quote-locked-banner">
					<template v-if="quoteData.orderNumber">
						This quote became Order #{{ quoteData.orderNumber }}<template v-if="quoteData.orderDate"> on {{ formatBannerDate(quoteData.orderDate) }}</template>.
						It is the snapshot as approved and can no longer be edited; later changes live on the order.
						<router-link
							v-if="quoteData.order && quoteData.order.salesOrderId"
							:to="{ path: `${orderBasePath}/${quoteData.order.salesOrderId}` }"
							class="ml-2"
						>Open the order</router-link>
					</template>
					<template v-else>
						This quote is cancelled and cannot be edited. Restore it to make changes.
					</template>
				</v-alert>
			</v-col>
		</v-row>
		<v-row id="detail-container"
			><v-col cols="12" class="flex-none">
				<div class="quote-form">
					<template v-if="quoteData.customerId == null">
						<v-row>
							<v-col class="quote-form-header">
								<div :class="['form-input', saveErrors.customerId ? 'is-invalid' : '']">
									<v-combobox
										prepend-inner-icon="mdi-magnify"
										dense
										outlined
										clearable
										placeholder="Search Customers"
										hide-details="auto"
										v-model="customerSearch"
										:search-input.sync="customerSearchInput"
										:items="customerResults"
										item-text="organization"
										:error="saveErrors.customerId"
										:error-messages="saveErrors.customerId"
										@change="getCustomerInfo(customerSearch.customerId)"
										@keydown.enter="handleCustomerSearchKeydown"
										@keydown.tab="handleCustomerSearchKeydown"
										autofocus
										ref="customerSearchInput"
									></v-combobox>
								</div>
								<span class="ml-2">
									or
									<router-link :to="`/relationships/customer/new?initToNewCustomerView=true`"
										>create a new customer</router-link
									>
								</span>
							</v-col>
						</v-row>
						<v-row>
							<v-col class="quote-form-header">
								<div class="form-input">
									<v-combobox
										prepend-inner-icon="mdi-magnify"
										dense
										outlined
										clearable
										placeholder="Search Contacts"
										hide-details="auto"
										v-model="contactSearch"
										:search-input.sync="contactSearchInput"
										:items="contactResults"
										:item-text="itemText"
										:error="saveErrors.customerId"
										:error-messages="saveErrors.customerId"
										@change="getCustomerInfo(contactSearch.customerId)"
										@keydown.enter="handleContactSearchKeydown"
										@keydown.tab="handleContactSearchKeydown"
									></v-combobox>
								</div>
							</v-col>
						</v-row>
					</template>
					<template v-else>
						<v-row>
							<v-col class="quote-form-header">
								<h1 class="quote-form-title">
									{{ quoteData.organization }}
								</h1>
								<div class="quote-form-status" :class="renderStatusColor(quoteData.status)">
									{{ formatStatus(quoteData.status) }}
								</div>
								<v-chip
									v-if="hasSampleItems"
									small
									color="#1565c0"
									text-color="white"
									class="ml-2"
								>
									Sample
								</v-chip>
							</v-col>
							<v-col>
								<div class="quote-actions">
									<v-btn
										v-if="isOpenQuote"
										depressed
										outlined
										:loading="isSaving"
										:disabled="isSaving || !saveButtonEnabled"
										@click="validateQuote(false)"
									>Save</v-btn>
									<v-btn
										v-if="canSubmitForApproval()"
										depressed
										color="success"
										@click="submitForApproval()"
										:disabled="isSubmitting || !placeOrderButtonEnabled"
										:loading="isSubmitting"
									>
										Submit for Approval
									</v-btn>
									<v-btn
										v-if="canRevert()"
										depressed
										outlined
										@click="revertToQuote()"
										:disabled="isReverting"
										:loading="isReverting"
									>Revert to Quote</v-btn>
									<v-btn
										v-if="canApprove()"
										depressed
										color="success"
										@click="approveQuote()"
										:disabled="isApproving"
										:loading="isApproving"
									>
										Approve
									</v-btn>
									<!-- Inline buttons when 2 or fewer overflow actions -->
									<template v-if="overflowActionCount <= 2">
										<v-btn
											depressed
											outlined
											@click="$router.push(`/sales-orders/quotes/${quoteData.quoteId}/print`)"
										>Print</v-btn>
										<v-btn
											v-if="canUsePayments()"
											depressed
											outlined
											@click="$router.push(`/sales-orders/quotes/${quoteData.quoteId}/payments`)"
										>Payments</v-btn>
										<v-btn
											v-if="quoteData.quoteId"
											depressed
											outlined
											@click="showAuditHistory = true"
										>Audit History</v-btn>
										<v-btn
											depressed
											outlined
											@click="handleClose()"
										>Close</v-btn>
										<v-tooltip
											v-if="canCancelQuote() && quoteData.quoteId && !quoteData.isCancelled"
											bottom
											:disabled="!quoteData.orderNumber || quoteData.orderNumber.length === 0"
										>
											<template v-slot:activator="{ on }">
												<div v-on="on">
													<v-btn
														depressed
														outlined
														:disabled="quoteData.orderNumber?.length > 0"
														@click="handleCancelQuote(true)"
													>Cancel Quote</v-btn>
												</div>
											</template>
											<span>This quote has an associated order. Cancel the order first.</span>
										</v-tooltip>
										<v-tooltip
											v-if="canRestoreQuote() && quoteData.quoteId && quoteData.isCancelled"
											bottom
											:disabled="!quoteData.orderNumber || quoteData.orderNumber.length === 0"
										>
											<template v-slot:activator="{ on }">
												<div v-on="on">
													<v-btn
														depressed
														outlined
														:disabled="quoteData.orderNumber?.length > 0"
														@click="handleCancelQuote(false)"
													>Restore Quote</v-btn>
												</div>
											</template>
											<span>This quote has an associated order. Restore the order first.</span>
										</v-tooltip>
									</template>
									<!-- Overflow menu when more than 2 actions -->
									<v-menu v-else offset-y left nudge-bottom="4" content-class="quote-overflow-menu">
										<template v-slot:activator="{ on, attrs }">
											<v-btn depressed outlined class="kebab-btn" v-bind="attrs" v-on="on">
												<v-icon>mdi-dots-vertical</v-icon>
											</v-btn>
										</template>
										<v-list>
											<v-list-item
												@click="$router.push(`/sales-orders/quotes/${quoteData.quoteId}/print`)"
											>
												<v-list-item-icon><v-icon>mdi-printer-outline</v-icon></v-list-item-icon>
												<v-list-item-title>Print</v-list-item-title>
											</v-list-item>
											<v-list-item
												v-if="canUsePayments()"
												@click="$router.push(`/sales-orders/quotes/${quoteData.quoteId}/payments`)"
											>
												<v-list-item-icon><v-icon>mdi-credit-card-outline</v-icon></v-list-item-icon>
												<v-list-item-title>Payments</v-list-item-title>
											</v-list-item>
											<v-list-item
												v-if="quoteData.quoteId"
												@click="showAuditHistory = true"
											>
												<v-list-item-icon><v-icon>mdi-history</v-icon></v-list-item-icon>
												<v-list-item-title>Audit History</v-list-item-title>
											</v-list-item>
											<v-divider></v-divider>
											<v-list-item
												@click="handleClose()"
											>
												<v-list-item-icon><v-icon>mdi-arrow-left</v-icon></v-list-item-icon>
												<v-list-item-title>Close</v-list-item-title>
											</v-list-item>
											<v-divider></v-divider>
											<v-list-item
												v-if="canCancelQuote() && quoteData.quoteId && !quoteData.isCancelled"
												:disabled="quoteData.orderNumber?.length > 0"
												:title="quoteData.orderNumber?.length > 0 ? 'This quote has an associated order. Cancel the order first.' : ''"
												@click="handleCancelQuote(true)"
											>
												<v-list-item-icon><v-icon class="red--text">mdi-cancel</v-icon></v-list-item-icon>
												<v-list-item-title class="red--text">Cancel Quote</v-list-item-title>
											</v-list-item>
											<v-list-item
												v-if="canRestoreQuote() && quoteData.quoteId && quoteData.isCancelled"
												:disabled="quoteData.orderNumber?.length > 0"
												:title="quoteData.orderNumber?.length > 0 ? 'This quote has an associated order. Restore the order first.' : ''"
												@click="handleCancelQuote(false)"
											>
												<v-list-item-icon><v-icon class="green--text">mdi-restore</v-icon></v-list-item-icon>
												<v-list-item-title class="green--text">Restore Quote</v-list-item-title>
											</v-list-item>
										</v-list>
									</v-menu>
								</div>
							</v-col>
						</v-row>

						<v-row>
							<v-col cols="4">
								<div class="form-input">
									<label>Contact</label>
									<v-select
										v-model="quoteData.contactId"
										:items="contacts"
										item-text="firstName"
										item-value="contactId"
										dense
										outlined
										hide-details="auto"
										:disabled="readonly || isLockedQuote"
									>
										<template v-slot:selection="{ item }">
											<v-row no-gutters align="center">
												<span>{{ `${item.firstName} ${item.lastName}` }}</span>
											</v-row>
										</template>
										<template v-slot:item="{ item, attrs, on }">
											<v-list-item v-on="on" v-bind="attrs">
												<v-list-item-content>
													<v-list-item-title>
														<v-row no-gutters align="center">
															<span>{{ `${item.firstName} ${item.lastName}` }}</span>
														</v-row>
													</v-list-item-title>
												</v-list-item-content>
											</v-list-item>
										</template>
									</v-select>
									<p v-if="errors.quoteData.contactId" class="form-error">
										* Please select a contact
									</p>
								</div>
							</v-col>

							<v-col>
								<div class="form-input">
									<label>Customer #</label>
									<v-text-field
										outlined
										dense
										hide-details="auto"
										v-model="quoteData.customerNumber"
										disabled
									></v-text-field>
								</div>
							</v-col>

							<v-col>
								<div class="form-input">
									<label>Quote #</label>
									<v-text-field
										outlined
										dense
										hide-details="auto"
										v-model="quoteData.quoteNumber"
										disabled
									></v-text-field>
								</div>
							</v-col>

							<v-col>
								<div class="form-input">
									<label>Order #</label>
									<v-text-field
										outlined
										dense
										hide-details="auto"
										v-model="quoteData.orderNumber"
										disabled
									>
										<template v-if="quoteData?.order?.salesOrderId" v-slot:append-outer>
											<v-tooltip bottom>
												<template v-slot:activator="{ on }">
													<router-link
														:to="{
															path: `${orderBasePath}/${quoteData?.order?.salesOrderId}`,
														}"
														><v-icon class="to-order" v-on="on">
															mdi-open-in-new
														</v-icon></router-link
													>
												</template>
												Go to Order
											</v-tooltip>
										</template>
									</v-text-field>
								</div>
							</v-col>

							<v-col>
								<div class="form-input">
									<label>School PO #</label>
									<v-text-field
										outlined
										dense
										hide-details="auto"
										v-model="quoteData.schoolPo"
										:disabled="readonly || isLockedQuote"
									></v-text-field>
								</div>
							</v-col>
						</v-row>
						<v-row>
							<v-col cols="4">
								<div class="form-input">
									<label>Bill To
										<router-link v-if="getCustomerId"
											:to="{ path: `/relationships/customer/${getCustomerId}`, query: { addressTab: 'true', initToNewCustomerView: 'true', ...addressReturnQuery } }">
											<v-icon small class="ml-1">mdi-open-in-new</v-icon>
										</router-link>
									</label>
									<v-select
										v-model="quoteData.billToId"
										:items="computedBillToAddr"
										item-value="addressId"
										dense
										outlined
										hide-details="auto"
										:disabled="readonly || isLockedQuote"
										:error="saveErrors.billToId"
										:error-messages="saveErrors.billToId"
									>
										<template v-slot:selection="{ item }">
											<span class="text-truncate d-inline-block" style="max-width: 100%">{{ item.text }}</span>
										</template>
										<template v-slot:item="{ item, on, attrs }">
											<v-list-item v-bind="attrs" v-on="on" class="address-dropdown-item">
												<v-list-item-content>
													<v-list-item-title v-if="item.contact" class="font-weight-bold">
														{{ item.contact.firstName }} {{ item.contact.lastName }}
													</v-list-item-title>
													<div v-if="item.organization" style="font-size: 1.4rem">{{ item.organization }}</div>
													<div v-if="item.department" style="font-size: 1.4rem">{{ item.department }}</div>
													<div style="font-size: 1.4rem">{{ renderAddress(item) }}</div>
													<div v-if="item.lastUsed" style="font-size: 1.2rem; color: grey">
														Last used: {{ formatDate(item.lastUsed) }}
													</div>
												</v-list-item-content>
											</v-list-item>
										</template>
										<template v-slot:no-data>
											<v-list-item>
												<v-list-item-content>
													<v-list-item-title>
														No addresses available.
														<router-link
															:to="{
																path: `/relationships/customer/${getCustomerId}`,
																query: {
																	addressTab: true,
																	initToNewCustomerView: true,
																	quoteId: quoteData.quoteId,
																},
															}"
															>Click here to add one.</router-link
														>
													</v-list-item-title>
												</v-list-item-content>
											</v-list-item>
										</template>
									</v-select>
									<p v-if="errors.quoteData.billToId" class="form-error">
										* Please select a billing address
									</p>
								</div>
							</v-col>

							<v-col cols="2">
								<div class="form-input">
									<label>Created Date</label>
									<v-menu
										v-model="showCreatedAt"
										:close-on-content-click="false"
										transition="scale-transition"
										offset-y
										min-width="290px"
										:disabled="readonly || isLockedQuote"
									>
										<template v-slot:activator="{ on, attrs }">
											<v-text-field
												v-model="formattedCreatedAt"
												append-icon="mdi-calendar"
												@click:append="showCreatedAt = true"
												outlined
												dense
												v-bind="attrs"
												v-on="on"
												@blur="quoteData.createdAt = parseDate(formattedCreatedAt)"
												hide-details="auto"
												:disabled="readonly || isLockedQuote"
											></v-text-field>
										</template>
										<v-date-picker
											v-model="quoteData.createdAt"
											@input="showCreatedAt = false"
										></v-date-picker>
									</v-menu>
								</div>
							</v-col>

							<v-col cols="2">
								<div class="form-input">
									<label>Ref #</label>
									<v-text-field
										outlined
										dense
										hide-details="auto"
										v-model="quoteData.referenceNumber"
										:disabled="readonly || isLockedQuote"
									></v-text-field>
								</div>
							</v-col>

							<v-col cols="2">
								<div class="form-input">
									<label>Attribution</label>
									<v-select
										v-model="quoteData.attribution"
										:items="attribuitions"
										item-text="text"
										item-value="text"
										dense
										outlined
										hide-detail
										:disabled="readonly || isLockedQuote"
									></v-select>
								</div>
							</v-col>

							<v-col cols="2">
								<div class="form-input">
									<label>Payment Terms</label>
									<v-select
										v-model="quoteData.paymentTermsId"
										:items="paymentTerms"
										item-text="paymentTermsName"
										item-value="paymentTermsId"
										dense
										outlined
										hide-detail
										:error="saveErrors.paymentTermsId"
										:error-messages="saveErrors.paymentTermsId"
										@change="saveErrors.paymentTermsId = undefined"
										:disabled="readonly || isLockedQuote"
									></v-select>
									<p v-if="errors.quoteData.paymentTermsId" class="form-error">
										* Please select a payment term
									</p>
								</div>
							</v-col>

							<v-col cols="4">
								<div class="form-input">
									<label>Ship To
										<router-link v-if="getCustomerId"
											:to="{ path: `/relationships/customer/${getCustomerId}`, query: { addressTab: 'true', initToNewCustomerView: 'true', ...addressReturnQuery } }">
											<v-icon small class="ml-1">mdi-open-in-new</v-icon>
										</router-link>
									</label>
									<v-select
										v-model="quoteData.shipToId"
										:items="computedShipToAddr"
										item-value="addressId"
										dense
										outlined
										hide-details="auto"
										:disabled="readonly || isLockedQuote"
										:error="saveErrors.shipToId"
										:error-messages="saveErrors.shipToId"
									>
										<template v-slot:selection="{ item }">
											<span class="text-truncate d-inline-block" style="max-width: 100%">{{ item.text }}</span>
										</template>
										<template v-slot:item="{ item, on, attrs }">
											<v-list-item v-bind="attrs" v-on="on" class="address-dropdown-item">
												<v-list-item-content>
													<v-list-item-title v-if="item.contact" class="font-weight-bold">
														{{ item.contact.firstName }} {{ item.contact.lastName }}
													</v-list-item-title>
													<div v-if="item.organization" style="font-size: 1.4rem">{{ item.organization }}</div>
													<div v-if="item.department" style="font-size: 1.4rem">{{ item.department }}</div>
													<div style="font-size: 1.4rem">{{ renderAddress(item) }}</div>
													<div v-if="item.lastUsed" style="font-size: 1.2rem; color: grey">
														Last used: {{ formatDate(item.lastUsed) }}
													</div>
												</v-list-item-content>
											</v-list-item>
										</template>
										<template v-slot:no-data>
											<v-list-item>
												<v-list-item-content>
													<v-list-item-title>
														No addresses available.
														<router-link
															:to="{
																path: '/relationships/customer',
																query: {
																	customerId: getCustomerId,
																	addressTab: true,
																	...(!quoteData.quoteNumber || quoteData.quoteNumber.trim() === ''
																		? {}
																		: { quoteNumber: quoteData.quoteNumber }),
																},
															}"
															>Click here to add one.</router-link
														>
													</v-list-item-title>
												</v-list-item-content>
											</v-list-item>
										</template>
									</v-select>
									<p v-if="errors.quoteData.shipToId" class="form-error">
										* Please select a shipping address
									</p>
								</div>
							</v-col>

							<v-col cols="2">
								<div class="form-input">
									<label>Expected Order Date</label>
									<v-menu
										v-model="showExpectedOrderDate"
										:disabled="readonly || isLockedQuote"
										:close-on-content-click="false"
										transition="scale-transition"
										offset-y
										min-width="290px"
									>
										<template v-slot:activator="{ on, attrs }">
											<v-text-field
												v-model="formattedExpectedOrderDate"
												append-icon="mdi-calendar"
												@click:append="showExpectedOrderDate = true"
												outlined
												dense
												v-bind="attrs"
												v-on="on"
												@blur="quoteData.expectedOrderDate = parseDate(formattedExpectedOrderDate)"
												hide-details="auto"
												:disabled="readonly || isLockedQuote"
											></v-text-field>
										</template>
										<v-date-picker
											v-model="quoteData.expectedOrderDate"
											@input="showExpectedOrderDate = false"
										></v-date-picker>
									</v-menu>
									<p v-if="errors.quoteData.expectedOrderDate" class="form-error">
										* Expected Order Date is required
									</p>
								</div>
							</v-col>

							<v-col cols="2">
								<div class="form-input">
									<label>Sales Rep</label>
									<v-select
										v-model="quoteData.salesRepId"
										:items="salesReps"
										item-text="name"
										item-value="salesRepId"
										:item-disabled="(item) => item.inactiveDate"
										dense
										outlined
										hide-detail
										:error="saveErrors.salesRepId"
										:error-messages="saveErrors.salesRepId"
										@change="saveErrors.salesRepId = undefined"
										:disabled="readonly || isLockedQuote"
									>
										<template v-slot:item="{ item }">
											<v-list-item-content>
												<v-list-item-title :class="{ 'text--disabled': item.inactiveDate }">
													<v-row no-gutters align="center">
														<span>{{ item.name }}</span>
														<v-icon v-if="item.inactiveDate" small color="grey" class="ml-2">
															mdi-account-off
														</v-icon>
													</v-row>
												</v-list-item-title>
											</v-list-item-content>
										</template>
										<template v-slot:selection="{ item }">
											<v-row no-gutters align="center">
												<span :class="{ 'text--disabled': item.inactiveDate }">{{
													item.name
												}}</span>
												<v-icon v-if="item.inactiveDate" small color="grey" class="ml-2">
													mdi-account-off
												</v-icon>
											</v-row>
										</template>
									</v-select>
									<p v-if="errors.quoteData.salesRepId" class="form-error">
										* Please select a sales rep
									</p>
								</div>
							</v-col>

							<v-col cols="2">
								<div class="form-input">
									<label>Shipping Method</label>
									<v-select
										v-model="quoteData.shippingMethodId"
										:items="shippingMethods"
										item-text="shippingMethodName"
										item-value="shippingMethodId"
										dense
										outlined
										hide-details="auto"
										:error="saveErrors.shippingMethodId"
										:error-messages="saveErrors.shippingMethodId"
										@change="
											() => {
												saveErrors.shippingMethodId = undefined;
												calculateShippingAndHandling(true);
											}
										"
										:disabled="readonly || isLockedQuote"
									></v-select>
									<p v-if="errors.quoteData.shippingMethodId" class="form-error">
										* Please select a shipping method
									</p>
								</div>
							</v-col>

							<v-col cols="2">
								<div class="form-input">
									<label>Sales Tax ID</label>
									<v-select
										v-model="quoteData.salesTaxId"
										:items="taxTypes"
										item-text="text"
										item-value="salesTaxId"
										dense
										outlined
										hide-detail
										disabled
									></v-select>
								</div>
							</v-col>
						</v-row>
					</template>
				</div> </v-col
		></v-row>
		<v-row id="sales-entry-table-container">
			<v-col cols="12" class="flex-auto">
				<p v-if="itemDataIncomplete" class="form-error text-center">
					* One or more items are missing requirments
				</p>
				<SalesEntryTable
					:loading="loadingTableData"
					v-model="quoteData.items"
					:maxHeight="computedTableHeight"
					:locked="isLockedQuote"
					@input="updateQuoteItems($event)"
					@onSave="handleSave"
				/>
			</v-col>
		</v-row>
		<v-row id="sales-footer-container">
			<v-col cols="12" class="flex-none">
				<div class="quote-notes">
					<div class="quote-notes-heading">
						<h2>Order Notes</h2>
						<template v-if="showNoteDropdown">
							<v-btn :disabled="!canUseNotes()" depressed outlined @click="showNoteDropdown = false"
								>Cancel</v-btn
							>
							<v-btn :disabled="!canUseNotes()" depressed outlined @click="saveNote()">Save</v-btn>
						</template>
						<v-btn v-else :disabled="!canUseNotes()" depressed outlined @click="newNote()"
							>New</v-btn
						>
					</div>
					<div
						class="quote-notes-dropdown"
						:class="showNoteDropdown ? 'quote-notes-dropdown-active' : null"
					>
						<div class="form-textarea">
							<label>Note</label>
							<v-textarea
								outlined
								hide-details="auto"
								rows="14"
								v-model="noteData.text"
								height="16rem"
							></v-textarea>
						</div>
						<div class="quote-notes-dropdown-author">
							<h3>Added By:</h3>
							<p>{{ noteData.createdBy }}</p>
						</div>
					</div>
					<div class="quote-notes-container">
						<div class="quote-notes-container-list">
							<div
								v-for="note in quoteData.notes"
								:key="note.noteId"
								class="quote-notes-container-note"
							>
								<span
									class="quote-notes-container-note-type"
									:class="note.noteType == 'USER' ? 'quote-notes-container-note-type-2' : null"
									>{{ note.noteType == "USER" ? "User Note" : "System Note" }}</span
								>
								<p>{{ note.text }}</p>
								<div class="quote-notes-container-note-author">
									{{ formatDateLocal(note.noteDate) }} - {{ note.createdByUser }}
								</div>
							</div>
						</div>
					</div>
				</div>

				<div class="quote-pricing">
					<v-row>
						<v-col>
							<div class="form-input">
								<label>Payments:</label>
								<v-text-field
									class="number-input price-input"
									prepend-inner-icon="mdi-currency-usd"
									flat
									solo
									dense
									hide-details="auto"
									readonly
									:value="computedPayments"
								></v-text-field>
							</div>
							<div class="form-input">
								<label>Balance:</label>
								<v-text-field
									class="number-input price-input"
									prepend-inner-icon="mdi-currency-usd"
									flat
									solo
									dense
									hide-details="auto"
									readonly
									:value="computedBalance"
								></v-text-field>
							</div>
						</v-col>
						<v-col
							><div class="form-input">
								<label>Subtotal:</label>
								<v-text-field
									class="number-input price-input"
									prepend-inner-icon="mdi-currency-usd"
									flat
									solo
									dense
									hide-details="auto"
									readonly
									:value="computedSubtotal"
								></v-text-field>
							</div>
							<div class="form-input">
								<label>
									<v-btn
										v-if="shippingAndHandlingManualOverride"
										small
										text
										color="primary"
										@click="calculateShippingAndHandling(true)"
										:disabled="isLockedQuote"
										class="ml-2"
										style="min-width: auto"
										title="Recalculate S&H"
									>
										<v-icon small>mdi-refresh</v-icon>
									</v-btn>
									S&amp;H:
								</label>
								<currency-text-field 
									:value="shippingAndHandling"
									:disabled="isLockedQuote"
									@input="onShippingAndHandlingInput"
									:is-manual-override="shippingAndHandlingManualOverride"
									:tooltip="shippingAndHandlingManualOverride ? 'Manually overridden - Click refresh to recalculate' : ''"
								/>
							</div>
							<div class="form-input">
								<label>S&amp;H Discount:</label>
								<currency-text-field v-model="shippingAndHandlingDiscount" :treatAsCredit="true" :disabled="isLockedQuote" />
							</div>
							<div class="form-input">
								<label>Tax Rate:</label>
								<v-text-field
									class="number-input price-input tax-rate-input"
									flat
									solo
									dense
									hide-details="auto"
									append-icon="mdi-percent"
									:readonly="!taxRateEditMode || isLockedQuote"
									:value="computedTaxRate"
									@input="onTaxRateInput"
									:title="taxRateManualOverride ? 'Manually overridden - Click refresh to recalculate' : ''"
									:class="{ 'manual-override': taxRateManualOverride }"
								>
									<template v-slot:prepend-inner v-if="canAdjustTaxRate()">
										<v-icon
											v-if="!taxRateManualOverride"
											small
											class="tax-rate-edit-icon"
											@click="enableTaxRateEdit"
											:disabled="isLockedQuote"
											title="Click to manually override tax rate"
										>mdi-pencil</v-icon>
										<v-icon
											v-else
											small
											color="primary"
											class="tax-rate-edit-icon"
											@click="resetTaxRateOverride"
											:disabled="isLockedQuote"
											title="Recalculate Tax Rate from ZIP"
										>mdi-refresh</v-icon>
									</template>
								</v-text-field>
							</div>
							<div class="form-input">
								<label>Tax Amount:</label>
								<v-text-field
									class="number-input price-input"
									prepend-inner-icon="mdi-currency-usd"
									flat
									solo
									dense
									hide-details="auto"
									readonly
									:value="computedTax"
								></v-text-field>
							</div>
							<div class="form-input">
								<label>Order Total:</label>
								<v-text-field
									class="number-input price-input"
									prepend-inner-icon="mdi-currency-usd"
									flat
									solo
									dense
									hide-details="auto"
									readonly
									:value="computedOrdertotal"
								></v-text-field></div
						></v-col>
					</v-row>

					<div v-if="isOpenQuote" class="quote-actions mt-4">
						<v-btn
							depressed
							outlined
							:loading="isSaving"
							:disabled="isSaving || !saveButtonEnabled"
							@click="validateQuote(false)"
						>Save</v-btn>
					</div>
				</div>
			</v-col>
		</v-row>

		<template>
			<v-overlay :dark="false" :value="showModal">
				<div class="input-modal warning-modal">
					<div class="input-modal-header warning-modal-header">
						<h1 class="input-modal-header-title warning-modal-title">WARNING</h1>
						<v-btn icon @click="cancelAction">
							<v-icon v-text="'mdi-close'"></v-icon>
						</v-btn>
					</div>
					<div class="input-modal-content">
						<div class="warning-modal-message">
							<p>Item is not part of defined sublist.</p>
							<p>Would you like to add a surcharge to the order?</p>
						</div>

						<ul class="warning-modal-list">
							<li key="descr">
								<v-text-field
									outlined
									dense
									hide-details="auto"
									v-model="selectedLineItem.upChargeDescription"
									label="Description (optional)"
									placeholder="Package Upgrade"
								></v-text-field>
							</li>
							<li key="upCharge">
								<v-text-field
									outlined
									dense
									hide-details="auto"
									v-model="selectedLineItem.upCharge"
									label="Amount"
									prefix="$"
									placeholder="0.00"
								></v-text-field>
							</li>
						</ul>

						<div class="input-modal-content-actions">
							<v-btn outlined depressed @click="cancelAction">Cancel</v-btn>
							<v-btn color="primary" depressed @click="confirmAction">Use this part</v-btn>
						</div>
					</div>
				</div>
			</v-overlay>
		</template>
		</v-container>

		<SalesPayments
			v-else-if="showSalesPaymentView"
			ref="salesPayments"
			:orderNumber="quoteData.orderNumber"
			:orderTotal="computedOrdertotal"
			:balance="computedBalance"
			:payments="quoteData.payments"
			@updatePayments="updatePayments"
			@cancel="() => $router.push(`/sales-orders/quotes/${quoteData.quoteId}`)"
		/>

		<SalesPrintTemplate
			v-else-if="showPrintPreview"
			printType="QUOTE"
			:printInfo="quoteData"
			@cancel="() => $router.push(`/sales-orders/quotes/${quoteData.quoteId}`)"
		/>

		<!-- Unsaved changes navigation warning dialog -->
		<ConfirmNavigationDialog
			:visible="showUnsavedChangesDialog"
			:loading="savingBeforeLeave"
			@stay="handleNavigationStay"
			@leave="handleNavigationLeave"
			@save-and-leave="handleNavigationSaveAndLeave"
		/>
		<ActivityHistoryDialog
			v-model="showAuditHistory"
			:quoteId="quoteData.quoteId"
			:orderId="quoteData.order ? quoteData.order.salesOrderId : null"
			:quoteNumber="quoteData.quoteNumber"
			:orderNumber="quoteData.orderNumber"
		/>
	</div>
</template>

<script>
import gql from "graphql-tag";
import DateFormatting from "@/mixins/DateFormatting";
import RateFormatting from "@/mixins/RateFormatting";
import UnsavedChangesGuard from "@/mixins/UnsavedChangesGuard";
import queries from "../../gql/queries";
import SalesEntryTable from "@/components/SalesOrders/SalesEntryTable.vue";
import SalesPayments from "@/components/SalesOrders/SalesPayments.vue";
import isEqual from "lodash/isEqual";
import SalesPrintTemplate from "@/components/SalesOrders/SalesPrintTemplate.vue";
import ConfirmNavigationDialog from "@/components/ConfirmNavigationDialog.vue";
import ActivityHistoryDialog from "@/components/SalesOrders/ActivityHistoryDialog.vue";
import { FULL_CUSTOMER_FRAGMENT, NOTES_INFO, SALES_PAYMENT_INFO } from "@/gql/fragments";
import { GET_TAX_RATE } from "@/gql/settings";
import { TAX_TYPES } from "@/helpers/constant";
import { fetchItemSizeColorData } from "@/helpers/itemSizeColorData";
import { hasValidColorSelections } from "../../DataTypes";
import { renderAddress } from "@/helpers";
import { getTotalPrice } from "@/helpers/PricingCalculator";
import { hasPermission, SystemPermissions } from "@/models/User";
import { PLACEHOLDER_SYSTEM_CATEGORY_ID } from "@/constants/LineItems";
import { quoteStatusKey } from "@/utils/quoteStatus";
const request = require("request");

export default {
	name: "QuoteView",
	mixins: [DateFormatting, RateFormatting, UnsavedChangesGuard],
	components: {
		SalesEntryTable,
		SalesPayments,
		SalesPrintTemplate,
		ConfirmNavigationDialog,
		ActivityHistoryDialog,
	},
	props: {
		quote: {
			type: Object,
			default() {
				return {
					quoteId: null,
				};
			},
		},
		initWithCustomer: {
			type: String,
			required: false,
		},
		pendingApprovalMode: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			// Placeholder item ID for draft quote line items
			PLACEHOLDER_ITEM_ID: -1,
			readonly: false,
			canUpdate: false,
			showAuditHistory: false,
			canCreate: false,
			loadingTableData: false,
			isSaving: false,
			isSubmitting: false,
			isApproving: false,
			isReverting: false,
			saveConfirmation: false,
			saveError: false,
			saveErrors: {},
			customerSearch: "",
			customerSearchInput: "",
			customerSearchTimeout: null,
			customerResults: [],
			contactSearch: "",
			contactSearchInput: "",
			contactSearchTimeout: null,
			contactResults: [],
			customerInfo: null,
			quoteData: {
				quoteId: null,
				status: "INCOMPLETE",
				organization: "",
				customerId: null,
				contactId: null,
				customerNumber: null,
				quoteNumber: null,
				orderNumber: null, //auto generated?
				schoolPo: "",
				billToId: null,
				shipToId: null,
				shippingMethodId: null,
				paymentTermsId: null,
				createdAt: "",
				processDate: "",
				orderDate: "",
				expectedOrderDate: "",
				salesRepId: null,
				referenceNumber: null,
				attribution: null,
				salesTaxId: null,
				orderTotal: 0.0,
				balance: 0.0,
				payments: [],
				items: [],
				notes: [],
				// pass these to print page
				printPrices: {},
				letterPositions: [],
				salesReps: [],
				paymentMethods: [],
				shippingMethods: [],
				paymentTerms: [],
			},
			shippingAndHandling: 0.0,
			shippingAndHandlingDiscount: 0.0,
			shippingAndHandlingManualOverride: false,
			taxRateManualOverride: false,
			taxRateEditMode: false,
			isCalculatingShipping: false,
			showNoteDropdown: false,
			noteData: {
				noteId: null,
				noteType: "USER",
				noteDate: "",
				text: "",
				createdBy: "",
			},
			showCreatedAt: false,
			formattedCreatedAt: this.formatDate(
				new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().substr(0, 10)
			),
			showExpectedOrderDate: false,
			formattedExpectedOrderDate: "",
			addresses: [],
			addressUsageMap: {},
			contacts: [],
			taxTypes: TAX_TYPES,
			shippingMethods: [],
			paymentTerms: [],
			paymentMethods: [],
			salesReps: [],
			attribuitions: [
				{ text: "NEW", attributionId: 1 },
				{ text: "REORDER", attributionId: 2 },
				{ text: "DEALER", attributionId: 3 },
				{ text: "REP", attributionId: 4 },
				{ text: "E-COMMISSION", attributionId: 5 },
				{ text: "OUTSIDE REP", attributionId: 6 },
				{ text: "WEB PAGE", attributionId: 7 },
				{ text: "UNKNOWN", attributionId: 8 },
				{ text: "RETURN", attributionId: 9 },
			],
			itemDataIncomplete: false,
			computedTaxRate: 0,
			computedSubtotal: 0,
			computedTableHeight: 350,
			savedShippingHandling: -1,
			originalItemCount: -1,
			shouldFullyValidate: false,
			errors: {
				quoteData: {
					customerId: false,
					contactId: false,
					shippingMethodId: false,
					paymentTermsId: false,
					billToId: false,
					shipToId: false,
					salesRepId: false,
					expectedOrderDate: false,
				},
				shippingAndHandling: false,
				shippingAndHandlingDiscount: false,
			},
			taxRateCache: {},
			showModal: false,
			selectedLineItem: {
				upCharge: null,
				upChargeDescription: "",
			},
			resolve: null,
			reject: null,
			maxRetries: 5,
			retryDelay: 1000,
			isLoaded: false,
			isBackNavigation: false,
		};
	},
	apollo: {
		salesReps: {
			query: queries.LIST_SALES_REPS,
			variables: {
				take: 500,
				offset: 0,
			},
			fetchPolicy: "no-cache",
			update: (data) => data.salesReps.items,
		},
		paymentTerms: {
			query() {
				return gql`
					query {
						paymentTerms(take: 500) {
							items {
								paymentTermsId
								paymentTermsName
							}
						}
					}
				`;
			},
			update: (data) => data.paymentTerms.items,
		},
		paymentMethods: {
			query() {
				return gql`
					query {
						paymentMethods(take: 500) {
							items {
								paymentMethodId
								paymentMethodName
							}
						}
					}
				`;
			},
			update: (data) => data.paymentMethods.items,
		},
		shippingMethods: {
			query: queries.GET_SHIPPING_METHODS,
			update: (data) => data.shippingMethods.items,
		},
		shippingMethodCalculations: {
			query() {
				return gql`
					query {
						shippingMethodCalculations(take: 201) {
							items {
								shippingMethodCalculationId
								rangeStart
								rangeEnd
								ground
								threeDay
								twoDay
								overnight
								sequence
							}
						}
					}
				`;
			},
			update: (data) => data.shippingMethodCalculations.items,
		},
		letterPositions: {
			query() {
				return gql`
					query {
						letterPosition(take: 500) {
							items {
								letterPositionId
								code
								description
							}
						}
					}
				`;
			},
			update: (data) => data.letterPosition.items,
		},
	},
	computed: {
		canViewPrinterSettings() {
			const user = this.$store.getters.userInfo;
			return user?.isAdmin || hasPermission(user, SystemPermissions.Settings_Printers_ALL);
		},
		/**
		 * Returns the base path for order navigation based on whether this is a sample order.
		 */
		orderBasePath() {
			return this.hasSampleItems ? '/sales-orders/sample-orders' : '/sales-orders/orders';
		},
		/**
		 * Reactively checks if current line items contain any sample items.
		 * Used to show/hide the Sample chip in the header in real-time.
		 */
		hasSampleItems() {
			if (!this.quoteData?.items || this.quoteData.items.length === 0) {
				return this.quoteData?.isSampleOrder || false;
			}
			const checkItems = (items) => {
				return items.some((item) => {
					if (item.itemId && item.itemId > 0 && this.$systemCategories.isSample(item.systemCategoryId)) {
						return true;
					}
					if (item.children && item.children.length > 0) {
						return checkItems(item.children);
					}
					return false;
				});
			};
			return checkItems(this.quoteData.items);
		},
		computedBillToAddr() {
			//filter out any customer addresses that are not labeled bill to or both
			return this.addresses
				.filter(
					(addr) => addr.label.toLowerCase() == "bill to" || addr.label.toLowerCase() == "both"
				)
				.map((obj) => ({
					...obj,
					text: this.formatAddressOption(obj),
					lastUsed: this.addressUsageMap[obj.addressId]?.lastUsedAsBillTo || null,
				}));
		},
		computedShipToAddr() {
			//filter out any customer addresses that are not labeled ship to or both
			return this.addresses
				.filter(
					(addr) => addr.label.toLowerCase() == "ship to" || addr.label.toLowerCase() == "both"
				)
				.map((obj) => ({
					...obj,
					text: this.formatAddressOption(obj),
					lastUsed: this.addressUsageMap[obj.addressId]?.lastUsedAsShipTo || null,
				}));
		},
		computedZipcodeState() {
			// Use Ship To address for tax calculation (tax is based on destination)
			const selectedTaxObj = this.computedShipToAddr.find(
				(el) => el.addressId == this.quoteData.shipToId
			);
			const zipCode = selectedTaxObj?.postalCode;
			const state = selectedTaxObj?.state;
			// Don't recalculate tax for pending approval quotes or manually overridden rates
			if (this.quoteData.status !== 'PENDING_APPROVAL' && !this.taxRateManualOverride && !this.isLockedQuote) {
				this.getTaxRate(zipCode, state);
			}
			return [zipCode, state];
		},
		computedOrdertotal() {
			let orderTotal = 0.0;
			const subtotal = parseFloat(this.computedSubtotal) || 0;
			const shippingHandling = parseFloat(this.shippingAndHandling) || 0;
			const shippingHandlingDiscount = parseFloat(this.shippingAndHandlingDiscount) || 0;
			const taxAmount = parseFloat(this.computedTax) || 0;

			orderTotal = subtotal + shippingHandling + shippingHandlingDiscount + taxAmount;

			return orderTotal.toFixed(2);
		},
		computedTax() {
			if (this.quoteData.salesTaxId === 0) {
				return 0.0;
			}

			// Sample orders are untaxed by default, regardless of the customer's
			// taxable designation, unless the rate was manually overridden (#1388)
			if (this.hasSampleItems && !this.taxRateManualOverride) {
				return "0.00";
			}

			const subtotal = parseFloat(this.computedSubtotal) || 0;
			const taxRate = parseFloat(this.computedTaxRate) || 0;
			const tax = (subtotal * taxRate) / 100;

			return tax.toFixed(2);
		},
		computedPayments() {
			let payments = 0.0;

			if (this.quoteData.payments && this.quoteData.payments.length > 0) {
				this.quoteData.payments.forEach((payment) => {
					const amount = parseFloat(payment.amount);
					if (!isNaN(amount)) {
						payments += amount;
					}
				});
			}

			return payments.toFixed(2);
		},
		computedBalance() {
			// Ensure we never return NaN
			const orderTotal = parseFloat(this.computedOrdertotal) || 0;
			const payments = parseFloat(this.computedPayments) || 0;
			const balance = orderTotal - payments;

			// Return 0 if calculation results in NaN
			if (isNaN(balance)) {
				return "0.00";
			}

			return balance.toFixed(2);
		},
		isOpenQuote() {
			return this.quoteData.status === 'INCOMPLETE' || this.quoteData.status === 'FINAL_CHECK';
		},
		// An approved quote is the snapshot as approved; the order is the live record (#1474).
		// Cancelled quotes are restored before they can change again.
		isLockedQuote() {
			return this.quoteData.status === 'ORDERED' || this.quoteData.status === 'CANCELLED' ||
				(this.quoteData.orderNumber?.length > 0);
		},
		// Overrides the UnsavedChangesGuard mixin: a locked quote has no quote save path, and its
		// payments and notes persist on their own, so leaving must never prompt to save (#1475).
		hasUnsavedChanges() {
			if (this.isLockedQuote) return false;
			if (!this.initialState) return false;
			return !isEqual(this.getTrackableState(), this.initialState);
		},
		// Address edits on a locked quote come back to the live record, the order (#1475).
		addressReturnQuery() {
			if (this.isLockedQuote && this.quoteData.order && this.quoteData.order.salesOrderId) {
				return { orderId: this.quoteData.order.salesOrderId };
			}
			return { quoteId: this.quoteData.quoteId || '' };
		},
		saveButtonEnabled() {
			if (this.quoteData.quoteId == null) {
				return this.canCreate;
			} else {
				return this.canUpdate;
			}
		},
		placeOrderButtonEnabled() {
			if (this.quoteData.quoteId == null) {
				return false;
			} else {
				return this.canPlaceOrder();
			}
		},
		getCustomerId() {
			return this.quoteData.customerId;
		},
		primaryView() {
			return !this.showSalesPaymentView && !this.showPrintPreview;
		},
		showSalesPaymentView() {
			return this.$route.path.endsWith("/payments") && this.isLoaded;
		},
		showPrintPreview() {
			return this.$route.path.endsWith("/print") && this.isLoaded;
		},
		overflowActionCount() {
			let count = 2; // Print and Close are always visible
			if (this.canUsePayments()) count++;
			if (this.canCancelQuote() && this.quoteData.quoteId && !this.quoteData.isCancelled) count++;
			if (this.canRestoreQuote() && this.quoteData.quoteId && this.quoteData.isCancelled) count++;
			return count;
		},
	},
	watch: {
		// Remove the automatic watcher - we'll handle manual detection differently
		"noteData.noteDate"(value) {
			this.formattedNoteDate = this.formatDate(value);
		},
		"quoteData.createdAt"(value) {
			this.formattedCreatedAt = this.formatDate(value);
		},
		"quoteData.expectedOrderDate"(value) {
			this.formattedExpectedOrderDate = this.formatDate(value);
		},
		"quoteData.shipToId": function (newVal) {
			// Use Ship To address for tax calculation (tax is based on destination)
			// Don't recalculate tax for pending approval quotes or manually overridden rates
			if (this.quoteData.status === 'PENDING_APPROVAL' || this.taxRateManualOverride || this.isLockedQuote) {
				return;
			}
			const selectedTaxObj = this.computedShipToAddr.find((el) => el.addressId == newVal);
			if (selectedTaxObj) {
				const zipCode = selectedTaxObj.postalCode;
				const state = selectedTaxObj.state;
				this.getTaxRate(zipCode, state);
			}
		},
		hasSampleItems(newVal) {
			// Keep the displayed tax rate in sync when items flip the order's sample status (#1388)
			if (this.quoteData.status === 'PENDING_APPROVAL' || this.taxRateManualOverride || this.isLockedQuote) {
				return;
			}
			if (newVal) {
				this.computedTaxRate = 0;
			} else {
				// Read the ship-to address directly; going through computedZipcodeState
				// would trigger its getTaxRate side effect and double the lookup
				const selectedTaxObj = this.computedShipToAddr.find(
					(el) => el.addressId == this.quoteData.shipToId
				);
				if (selectedTaxObj) {
					this.getTaxRate(selectedTaxObj.postalCode, selectedTaxObj.state);
				}
			}
		},
		"quoteData.shippingMethodId"(newVal, oldVal) {
			if (newVal && newVal !== oldVal && !this.shippingAndHandlingManualOverride) {
				this.savedShippingHandling = 0;
				this.calculateShippingAndHandling();
			}
		},
		computedSubtotal(newValue, oldValue) {
			// Recalculate S&H when subtotal changes (unless manually overridden)
			if (newValue !== oldValue && !this.shippingAndHandlingManualOverride) {
				// Reset savedShippingHandling to force recalculation
				this.savedShippingHandling = 0;
				this.calculateShippingAndHandling();
			}
		},
		computedBalance() {
			this.applyQuoteMeta();
		},
		quoteData: {
			handler() {
				this.calculateTableHeight();
			},
			deep: true,
		},
		primaryView(newVal) {
			if (newVal) {
				this.calculateTableHeight();
			}
		},
		async showSalesPaymentView(newVal) {
			// Refetch payments when opening the payments view
			if (newVal && this.quoteData.quoteId) {
				await this.refreshPayments();
			}
		},
		customerSearchInput(value) {
			// Debounce the search to prevent excessive API calls
			if (this.customerSearchTimeout) {
				clearTimeout(this.customerSearchTimeout);
			}
			this.customerSearchTimeout = setTimeout(() => {
				if (value && value.length > 0) {
					this.searchCustomers(value);
				}
			}, 300);
		},
		contactSearchInput(value) {
			// Debounce the search to prevent excessive API calls
			if (this.contactSearchTimeout) {
				clearTimeout(this.contactSearchTimeout);
			}
			this.contactSearchTimeout = setTimeout(() => {
				if (value && value.length > 0) {
					this.searchContacts(value);
				}
			}, 300);
		},
	},
	methods: {
		renderAddress,
		formatAddressOption(addr) {
			const contactName = addr.contact
				? `${addr.contact.firstName} ${addr.contact.lastName}`.trim()
				: '';
			const addrText = renderAddress(addr);
			return contactName ? `${contactName}: ${addrText}` : addrText;
		},
		updateQuoteItems(items) {
			this.computedSubtotal = getTotalPrice(items, 0).toFixed(2);
		},

		/**
		 * Returns the state that should be tracked for unsaved changes
		 * Required by UnsavedChangesGuard mixin
		 */
		getTrackableState() {
			return {
				quoteData: {
					quoteId: this.quoteData.quoteId,
					customerId: this.quoteData.customerId,
					contactId: this.quoteData.contactId,
					status: this.quoteData.status,
					expectedOrderDate: this.quoteData.expectedOrderDate,
					createdAt: this.quoteData.createdAt,
					shippingMethodId: this.quoteData.shippingMethodId,
					paymentMethodId: this.quoteData.paymentMethodId,
					paymentTermsId: this.quoteData.paymentTermsId,
					salesRepId: this.quoteData.salesRepId,
					billToId: this.quoteData.billToId,
					shipToId: this.quoteData.shipToId,
					attribution: this.quoteData.attribution,
					schoolPo: this.quoteData.schoolPo,
					referenceNumber: this.quoteData.referenceNumber,
					items: this.quoteData.items,
					payments: this.quoteData.payments,
					notes: this.quoteData.notes,
				},
				shippingAndHandling: this.shippingAndHandling,
				shippingAndHandlingDiscount: this.shippingAndHandlingDiscount,
				shippingAndHandlingManualOverride: this.shippingAndHandlingManualOverride,
			};
		},

		async saveBeforeLeave() {
			// A locked quote (ordered or cancelled) must never be re-saved from the leave prompt;
			// this path used to overwrite approved snapshots (#1474).
			if (this.isLockedQuote) {
				this.showNavigationWarning = false;
				return;
			}
			await this.saveQuote();
		},

		formatBannerDate(isoDate) {
			if (!isoDate) return "";
			const [year, month, day] = String(isoDate).split("-");
			return month && day && year ? `${month}/${day}/${year}` : isoDate;
		},

		// <security>
		canCreateQuote() {
			const user = this.$store.getters.userInfo;
			if (hasPermission(user, SystemPermissions.SalesOrders_Quote_Create)) {
				return true;
			}
			return user.isAdmin;
		},
		canUpdateQuote() {
			const user = this.$store.getters.userInfo;
			if (hasPermission(user, SystemPermissions.SalesOrders_Quote_Update)) {
				return true;
			}
			return user.isAdmin;
		},
		canCancelQuote() {
			const user = this.$store.getters.userInfo;
			// Only allow cancel on open (INCOMPLETE or FINAL_CHECK) quotes
			if (!this.isOpenQuote) {
				return false;
			}
			if (hasPermission(user, SystemPermissions.SalesOrders_Quote_Cancel)) {
				return true;
			}
			return user.isAdmin;
		},
		canRestoreQuote() {
			const user = this.$store.getters.userInfo;
			if (this.quoteData.status !== 'CANCELLED') {
				return false;
			}
			if (hasPermission(user, SystemPermissions.SalesOrders_Quote_Cancel)) {
				return true;
			}
			return user.isAdmin;
		},
		canPlaceOrder() {
			const user = this.$store.getters.userInfo;
			if (hasPermission(user, SystemPermissions.SalesOrders_Quote_PlaceOrder)) {
				return true;
			}

			return user.isAdmin;
		},
		canSubmitForApproval() {
			const user = this.$store.getters.userInfo;
			return this.isOpenQuote &&
				   !this.quoteData.orderNumber &&
				   (hasPermission(user, SystemPermissions.SalesOrders_Quote_PlaceOrder) || user.isAdmin);
		},
		canApprove() {
			const user = this.$store.getters.userInfo;
			return this.quoteData.status === 'PENDING_APPROVAL' &&
				   (hasPermission(user, SystemPermissions.SalesOrders_Quote_Approve) || user.isAdmin);
		},
		canRevert() {
			const user = this.$store.getters.userInfo;
			return this.quoteData.status === 'PENDING_APPROVAL' &&
				   (hasPermission(user, SystemPermissions.SalesOrders_Quote_Revert) || user.isAdmin);
		},
		canAdjustTaxRate() {
			const user = this.$store.getters.userInfo;
			// Allow tax rate adjustment on quotes that haven't been ordered yet
			return !this.quoteData.orderNumber &&
				   (hasPermission(user, SystemPermissions.SalesOrders_Quote_AdjustTaxRate) || user.isAdmin);
		},
		canUseNotes() {
			const user = this.$store.getters.userInfo;
			if (hasPermission(user, SystemPermissions.SalesOrders_Quote_Notes_ALL)) {
				return true;
			}
			return user.isAdmin;
		},
		canUsePayments() {
			const user = this.$store.getters.userInfo;
			if (hasPermission(user, SystemPermissions.SalesOrders_Quote_Payments_ALL)) {
				return true;
			}
			return user.isAdmin;
		},
		// </security>

		handleCustomerSearchKeydown(event) {
			// Only proceed if we have exactly one result
			if (this.customerResults.length === 1) {
				const singleResult = this.customerResults[0];
				this.customerSearch = singleResult;
				this.getCustomerInfo(singleResult.customerId);

				// Prevent default behavior for enter key
				if (event.key === "Enter") {
					event.preventDefault();
				}
			}
		},

		handleContactSearchKeydown(event) {
			// Only proceed if we have exactly one result
			if (this.contactResults.length === 1) {
				const singleResult = this.contactResults[0];
				this.contactSearch = singleResult;
				this.getCustomerInfo(singleResult.customerId);

				// Prevent default behavior for enter key
				if (event.key === "Enter") {
					event.preventDefault();
				}
			}
		},

		renderStatusColor(statusLabel) {
			let statusColor;
			switch (statusLabel) {
				case "INCOMPLETE":
					statusColor = "table-status-orange";
					break;
				case "FINAL_CHECK":
					statusColor = "table-status-light-blue";
					break;
				case "PENDING_APPROVAL":
					statusColor = "table-status-blue";
					break;
				case "CANCELED":
				case "CANCELLED":
					statusColor = "table-status-red";
					break;
				case "ORDERED":
					statusColor = "table-status-green";
					break;
				case "PENDING":
					statusColor = "table-status-orange";
					break;
				case "INPROCESS":
					statusColor = "table-status-orange";
					break;
				case "SHIPPED":
					statusColor = "table-status-green";
					break;
				default:
					statusColor = "";
			}

			return statusColor;
		},

		formatStatus(status) {
			if (!status) return "";
			const statusMap = {
				PENDING_APPROVAL: "Pending Approval",
				INCOMPLETE: "Incomplete",
				FINAL_CHECK: "Final Check",
				ORDERED: "Ordered",
				CANCELED: "Canceled",
				CANCELLED: "Cancelled",
				PENDING: "Pending",
				INPROCESS: "In Process",
				SHIPPED: "Shipped",
			};
			return statusMap[status] || status;
		},

		onShippingAndHandlingInput(value) {
			// A locked quote (ordered or cancelled) is a historical artifact (#1475)
			if (this.isLockedQuote) return;
			// This is a manual user input
			this.shippingAndHandling = value;
			this.shippingAndHandlingManualOverride = true;
		},

		enableTaxRateEdit() {
			if (this.isLockedQuote) return;
			// Enable edit mode for tax rate - user must click pencil to start editing
			this.taxRateEditMode = true;
		},

		onTaxRateInput(value) {
			if (this.isLockedQuote) return;
			// This is a manual user input for tax rate
			const numValue = parseFloat(value) || 0;
			this.computedTaxRate = numValue;
			this.taxRateManualOverride = true;
		},

		async resetTaxRateOverride() {
			if (this.isLockedQuote) return;
			// Reset the manual override and recalculate from ZIP
			this.taxRateManualOverride = false;
			this.taxRateEditMode = false;
			await this.getTaxRate(this.computedZipcodeState[0], this.computedZipcodeState[1]);
		},

		calculateShippingAndHandling(override = false) {
			// A locked quote keeps the S&H it was approved with (#1475)
			if (this.isLockedQuote) return;
			// Don't calculate if manually overridden (unless explicitly requested)
			if (this.shippingAndHandlingManualOverride && !override) {
				return this.shippingAndHandling;
			}

			// Check if we have all required data
			const hasRequiredData = 
				this.quoteData.shippingMethodId && 
				this.shippingMethodCalculations?.length > 0 && 
				this.shippingMethods?.length > 0;

			if (!hasRequiredData) {
				// Don't reset to 0 if data is missing
				console.warn('Missing data for S&H calculation:', {
					shippingMethodId: this.quoteData.shippingMethodId,
					hasCalculations: this.shippingMethodCalculations?.length > 0,
					hasMethods: this.shippingMethods?.length > 0
				});
				return this.shippingAndHandling || 0;
			}

			let addedNewItems = this.originalItemCount != this.quoteData.items.length;
			
			// Calculate when: new quote, override requested, or items changed
			if (this.savedShippingHandling <= 0 || override === true || addedNewItems) {
				const shippingCalculationGroup = this.shippingMethodCalculations?.find(
					(obj) => obj.rangeEnd >= this.computedSubtotal
				) || this.shippingMethodCalculations?.[this.shippingMethodCalculations.length - 1];
				const shippingMethodById = this.shippingMethods.find(
					(s) => s.shippingMethodId == this.quoteData.shippingMethodId
				);

				//generate the appropriate shipping charge based on the shipping method and subtotal price range
				let generatedShippingAndHandling = 0;
				if (shippingCalculationGroup && shippingMethodById) {
					switch (shippingMethodById.shippingCategoryId) {
						case 1:
							generatedShippingAndHandling = shippingCalculationGroup["ground"] || 0;
							break;
						case 2:
							generatedShippingAndHandling = shippingCalculationGroup["threeDay"] || 0;
							break;
						case 3:
							generatedShippingAndHandling = shippingCalculationGroup["twoDay"] || 0;
							break;
						case 4:
							generatedShippingAndHandling = shippingCalculationGroup["overnight"] || 0;
							break;
						default:
							generatedShippingAndHandling = shippingCalculationGroup["ground"] || 0;
							break;
					}
				}

				// Set the value programmatically (not manual)
				this.shippingAndHandling = generatedShippingAndHandling;
				this.shippingAndHandlingManualOverride = false; // Reset after calc
				
				return generatedShippingAndHandling;
			}

			return this.savedShippingHandling;
		},

		applyQuoteMeta() {
			this.setPricesForPrint();
			this.quoteData.paymentMethods = this.paymentMethods;
			this.quoteData.salesReps = this.salesReps;
			this.quoteData.shippingMethods = this.shippingMethods;
			this.quoteData.paymentTerms = this.paymentTerms;
		},

		setPricesForPrint() {
			let printPrices = {
				computedSubtotal: this.computedSubtotal,
				shippingAndHandling: this.shippingAndHandling,
				shippingAndHandlingDiscount: this.shippingAndHandlingDiscount,
				computedTaxRate: this.computedTaxRate,
				computedTaxAmount: this.computedTax,
				computedOrdertotal: this.computedOrdertotal,
				computedPayments: this.computedPayments,
				computedBalance: this.computedBalance,
			};

			this.quoteData.printPrices = printPrices;
		},

		async updatePayments(paymentsArr) {
			if (this._savingPayments) return;
			this._savingPayments = true;

			//add the passed in payments to the quote
			this.quoteData.payments = paymentsArr;

			//reset payment view state
			this.$router.push(`/sales-orders/quotes/${this.quoteData.quoteId}`);

			try {
				//if a quoteId exists in data save the payments (await to get assigned IDs before quote save)
				let paymentSaveSuccess = true;
				if (this.quoteData.quoteId) {
					paymentSaveSuccess = await this.saveQuotePayments(this.quoteData.quoteId);
				}

				// Only save the quote once the payments are persisted. Saving after a failed payment
				// PUT would write a balance that subtracts payments the server never stored (and
				// UpdateQuote propagates that balance to the order). Leaving the flag false means the
				// next Save re-sends the payments instead of skipping them.
				this._paymentsSavedSeparately = paymentSaveSuccess;
				// Payments are shared with the order and the server recomputes balances itself, so a
				// locked quote (ordered or cancelled) is not re-saved here: that re-save used to
				// overwrite the approved snapshot with whatever the line table held (#1474).
				if (paymentSaveSuccess && !this.isLockedQuote) {
					this.saveQuote();
				}
			} finally {
				this._savingPayments = false;
			}
		},

		newNote() {
			this.showNoteDropdown = true;
			this.noteData = {
				noteId: null,
				noteType: "USER",
				noteDate: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
					.toISOString()
					.substr(0, 10),
				text: "",
				createdBy: this.$store.getters.userInfo.username || this.$store.getters.userInfo.name,
			};
		},

		saveNote() {
			//add current active note to quote notes
			this.noteData.noteDate = `${this.noteData.noteDate} ${new Date().toLocaleTimeString()}`;
			this.quoteData.notes.push(this.noteData);

			this.showNoteDropdown = false;

			if (this.quoteData.quoteId) {
				this.saveQuoteNotes(this.quoteData.quoteId);
			} else {
				this.saveQuote();
			}
		},

		setCustomer() {
			//assign the selected customerInfo contacts and addresses
			this.contacts = this.customerInfo.contacts;
			this.addresses = this.customerInfo.addresses;

			//assign the selected customerInfo data to quoteData
			if (this.contacts.some((obj) => obj.primaryContact == true)) {
				//assign the first found primary contact
				this.quoteData.contactId = this.contacts.find(
					(obj) => obj.primaryContact == true
				).contactId;
			} else {
				//assign a default first contact
				this.quoteData.contactId = this.contacts[0].contactId;
			}

			if (this.computedBillToAddr.some((obj) => obj.isPrimaryAddress == true)) {
				//assign the first found primary bill to address
				this.quoteData.billToId = this.computedBillToAddr.find(
					(obj) => obj.isPrimaryAddress == true
				).addressId;
			} else {
				//assign the default first bill to address
				this.quoteData.billToId =
					this.computedBillToAddr && this.computedBillToAddr.length > 0
						? this.computedBillToAddr[0].addressId
						: null;
			}

			if (this.computedShipToAddr.some((obj) => obj.isPrimaryAddress == true)) {
				//assign the first found primary ship to address
				this.quoteData.shipToId = this.computedShipToAddr.find(
					(obj) => obj.isPrimaryAddress == true
				).addressId;
			} else {
				//assign the default first ship to address
				this.quoteData.shipToId =
					this.computedShipToAddr && this.computedShipToAddr.length > 0
						? this.computedShipToAddr[0].addressId
						: null;
			}

			this.quoteData.customerId = this.customerInfo.customerId;
			this.quoteData.organization = this.customerInfo.organization;
			this.quoteData.customerNumber = this.customerInfo.customerNumber;
			this.quoteData.shippingMethodId = this.customerInfo.shippingMethodId;
			this.quoteData.paymentTermsId = this.customerInfo.paymentTermsId;
			this.quoteData.createdAt = new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
				.toISOString()
				.substr(0, 10);
			//set date 2 weeks from createdAt date
			this.quoteData.expectedOrderDate = new Date(Date.now() + 12096e5).toISOString().substr(0, 10);
			this.quoteData.salesRepId = this.customerInfo.salesRepId;
			this.quoteData.salesTaxId = this.customerInfo.salesTaxId;

			// for the print page to match the object when the quote is retrieved
			this.quoteData.quoteCustomer = {
				contactId: this.quoteData.contactId,
				billTo: this.quoteData.billToId,
				shipTo: this.quoteData.shipToId,
				customer: {
					...this.customerInfo,
				},
			};

			this.$nextTick(() => {
				this.getTaxRate(this.computedZipcodeState[0], this.computedZipcodeState[1]);
			});
		},

		async searchCustomers(input) {
			//gql filter request of customers
			console.log("Getting customers named", input);
			try {
				const res = await this.$apollo.query({
					query: gql`
                        query {
                            searchCustomers(searchTerm: "${input}") {
                                items {
                                    customerId
                                    organization
                                }
                                pageInfo {
                                    hasNextPage
                                    hasPreviousPage
                                }
                            }
                        }
                    `,
				});
				this.customerResults = res.data.searchCustomers.items;
			} catch (error) {
				console.log(error);
			}
		},

		async searchContacts(input) {
			console.log("Getting contacts named", input);
			try {
				const where = {
					and: [
						{ isActive: { eq: true } },
						{
							or: [
								{ firstName: { contains: input } },
								{ lastName: { contains: input } },
								{ cellPhoneNumber: { contains: input } },
								{ workPhoneNumber: { contains: input } },
								{ email: { contains: input } },
								{ customer: { customerNumber: { contains: input } } },
							],
						},
					],
				};
				const res = await this.$apollo.query({
					query: gql`
						query GetContacts($pageOffset: Int!, $where: ContactFilterInput = null) {
							contacts(skip: $pageOffset, take: 15, where: $where, order: { firstName: ASC }) {
								items {
									contactId
									firstName
									lastName
									cellPhoneNumber
									workPhoneNumber
									email
									customerId
									customer {
										customerNumber
										organization
									}
								}
								pageInfo {
									hasNextPage
									hasPreviousPage
								}
							}
						}
					`,
					variables: {
						pageOffset: 0,
						where: where,
					},
					fetchPolicy: "no-cache",
				});
				this.contactResults = res.data.contacts.items;
			} catch (error) {
				console.log(error);
			}
		},

		async getCustomerInfo(customerId) {
			if (customerId == null) {
				return;
			}

			let retries = 0;
			while (retries < this.maxRetries) {
				try {
					const res = await this.$apollo.query({
						query: gql`query {
                        customerById(id: ${customerId}) {
                            customerId
                            organization
                            customerCategoryId
                            customerNumber
                            shippingMethodId
                            paymentTermsId
                            salesRepId
                            paymentMethodId
                            salesTaxId
                            addresses {
                                addressId
                                label
                                addressLine1
                                addressLine2
                                addressTypeId
                                city
                                state
                                postalCode
                                isPrimaryAddress
								isActive
								contactId
								organization
								department
								contact {
									contactId
									firstName
									lastName
									email
									workPhoneNumber
									cellPhoneNumber
								}
                            }
                            contacts {
                                contactId
                                primaryContact
                                firstName
                                lastName
                                workPhoneNumber
                                cellPhoneNumber
                                email
                                isActive
                            }
                        }
                    }`,
						fetchPolicy: "no-cache",
					});
					// Get the customer data
					const customerData = res.data.customerById;

					// Filter out inactive contacts and addresses
					if (customerData) {
						if (customerData.contacts) {
							customerData.contacts = customerData.contacts.filter((contact) => contact.isActive);
						}
						if (customerData.addresses) {
							customerData.addresses = customerData.addresses.filter((address) => address.isActive);
						}

						// Set the filtered customer data
						this.customerInfo = customerData;

						// Set the customer info on the quote
						this.setCustomer();

						// Fetch address usage data (non-blocking)
						this.fetchAddressUsage(customerId);

						// After setting customer info, focus the first row's style code field
						this.$nextTick(() => {
							// Find the first row's style code input field
							const firstRowInput = document.querySelector(
								".sales-table-body-wrapper .flex-table-row:first-child .v-text-field input"
							);
							if (firstRowInput) {
								firstRowInput.focus();
							}
						});

						// If we've successfully retrieved and set the customer info, exit the retry loop
						return;
					}
				} catch (error) {
					console.log(`Error fetching customer info (attempt ${retries + 1}):`, error);
					retries++;
					if (retries >= this.maxRetries) {
						console.error("Max retries reached. Unable to fetch customer info.");
					} else {
						await new Promise((resolve) => setTimeout(resolve, this.retryDelay));
					}
				}
			}
		},

		async fetchAddressUsage(customerId) {
			try {
				const res = await this.$apollo.query({
					query: gql`query {
						addressUsage(customerId: ${customerId}) {
							addressId
							lastUsedAsBillTo
							lastUsedAsShipTo
						}
					}`,
					fetchPolicy: "no-cache",
				});
				const usageMap = {};
				if (res.data.addressUsage) {
					for (const item of res.data.addressUsage) {
						usageMap[item.addressId] = {
							lastUsedAsBillTo: item.lastUsedAsBillTo,
							lastUsedAsShipTo: item.lastUsedAsShipTo,
						};
					}
				}
				this.addressUsageMap = usageMap;
			} catch (error) {
				console.log("Error fetching address usage:", error);
			}
		},

		async getQuoteInfo(quoteId) {
			try {
				this.loadingTableData = true;
				//create gql filter request to get quoteData by id
				const res = await this.$apollo.query({
					query: gql`
						${FULL_CUSTOMER_FRAGMENT}
						${NOTES_INFO}
						${SALES_PAYMENT_INFO}
						query GetQuoteById($id: Int!) {
							quoteById(id: $id) {
								quoteId
								createdAt
								expectedOrderDate
								paymentTermsId
								quoteNumber
								salesRepId
								shippingMethodId
								status
								attribution
								schoolPo
								referenceNumber
								shippingAndHandling
								shippingAndHandlingDiscount
								shippingAndHandlingManualOverride
								tax
								taxRate
								taxRateManualOverride
								total
								balance
								isCancelled
								isSampleOrder
								order {
									orderNumber
									salesOrderId
									orderDate
									processDate
								}
								quoteCustomer {
									contactId
									billTo
									shipTo
									customer {
										...FullCustomer
									}
								}
								payments {
									...SalesPaymentInfo
								}
								notes {
									...NotesInfo
								}
							}
						}
					`,
					fetchPolicy: "no-cache",
					variables: {
						id: quoteId,
					},
				});

				// Validate that quote has customer data
				const quoteCustomer = res.data.quoteById?.quoteCustomer;
				const customer = quoteCustomer?.customer;

				if (!quoteCustomer || !customer) {
					console.error('Quote is missing customer data:', {
						quoteId,
						hasQuoteCustomer: !!quoteCustomer,
						hasCustomer: !!customer
					});
					this.$toast?.error('Quote is missing customer information. Please select a customer.');
					this.loadingTableData = false;
					return;
				}

				//set customer addresses and contacts in state
				this.addresses = (customer.addresses || []).filter(
					(address) => address.isActive
				);
				this.contacts = (customer.contacts || []).filter(
					(contact) => contact.isActive
				);

				this.savedShippingHandling = res.data.quoteById.shippingAndHandling;
				this.shippingAndHandlingManualOverride = res.data.quoteById.shippingAndHandlingManualOverride || false;
				this.taxRateManualOverride = res.data.quoteById.taxRateManualOverride || false;
				this.taxRateEditMode = this.taxRateManualOverride; // Enable edit mode if already overridden

				let addressFromQueryParam = this.$route.query.addressLine1
					? this.addresses.find((a) => a.addressLine1 === this.$route.query.addressLine1)?.addressId
					: null;

				//set quote data
				this.quoteData = {
					...res.data.quoteById,
					organization: customer.organization,
					createdAt: res.data.quoteById.createdAt?.split("T").shift() || '',
					expectedOrderDate: res.data.quoteById.expectedOrderDate?.split("T").shift() || '',
					billToId: quoteCustomer.billTo || addressFromQueryParam,
					shipToId: quoteCustomer.shipTo || addressFromQueryParam,
					contactId: quoteCustomer.contactId,
					salesTaxId: customer.salesTaxId ?? 0,
					customerId: customer.customerId,
					customerNumber: customer.customerNumber,
					orderNumber: res.data.quoteById.order ? res.data.quoteById.order.orderNumber : null,
					orderDate: res.data.quoteById.order?.orderDate ? res.data.quoteById.order.orderDate.split("T").shift() : null,
					processDate: res.data.quoteById.order?.processDate ? res.data.quoteById.order.processDate.split("T").shift() : null,
					items: this.quoteData.items || [],
					notes: await Promise.all(
						res.data.quoteById.notes.map(async (note) => ({
							...note,
							author: note.createdBy,
							createdByUser: await this.getUserName(note.createdBy),
						}))
					),
					//missing
					// notes: [],
					// balance: "",
					// orderTotal: "",
				};

				//set shipping/discount prices
				this.shippingAndHandling = res.data.quoteById.shippingAndHandling;
				this.shippingAndHandlingDiscount = res.data.quoteById.shippingAndHandlingDiscount;

				//get the quotes items
				await this.getQuoteItems(quoteId);
				this.$nextTick(() => {
					// For pending approval quotes or manually overridden rates, use the stored tax rate
					// This prevents tax rate changes (e.g., from uploading a new Zip Tax Rate file) from affecting quotes that have been finalized
					if (this.quoteData.status === 'PENDING_APPROVAL' || this.taxRateManualOverride || this.isLockedQuote) {
						this.computedTaxRate = this.quoteData.taxRate || 0;
					} else {
						this.getTaxRate(this.computedZipcodeState[0], this.computedZipcodeState[1]);
					}
					// Capture initial state after all quote data is loaded
					this.captureInitialState();
				});
			} catch (error) {
				console.error('Error loading quote:', error);
				this.$toast?.error('Failed to load quote data. Please try again.');
				this.loadingTableData = false;
			}
		},

		async refreshPayments() {
			try {
				const res = await this.$apollo.query({
					query: gql`
						${SALES_PAYMENT_INFO}
						query GetQuoteById($id: Int!) {
							quoteById(id: $id) {
								payments {
									...SalesPaymentInfo
								}
								balance
							}
						}
					`,
					fetchPolicy: "no-cache",
					variables: {
						id: this.quoteData.quoteId,
					},
				});

				// Update only the payments and balance
				this.quoteData.payments = res.data.quoteById.payments ? res.data.quoteById.payments : [];
				this.quoteData.balance = res.data.quoteById.balance;
			} catch (error) {
				console.error("Error refreshing payments:", error);
			}
		},

		async getQuoteItems(quoteId) {
			this.loadingTableData = true;
			try {
				const res = await this.$http.get(`/api/quotes/${quoteId}/lineItems`);
				const quoteItems = await this.processItemsRecursively(res.data.lineItems);
				this.quoteData.items = quoteItems;
				this.originalItemCount = quoteItems.length;
				this.computedSubtotal = getTotalPrice(quoteItems, 0).toFixed(2);
				this.setPricesForPrint();
				this.loadingTableData = false;
			} catch (error) {
				console.log(error);
				this.loadingTableData = false;
			}
		},

		async processItemsRecursively(items) {
			return Promise.all(
				items.map(async (item) => {
					// Preserve package context fields before merging with inventory data
					const packageCategoryId = item.packageCategoryId;
					const subItemCategoryId = item.subItemCategoryId;

					let inventoryItem = await this.getQuoteItemSizeColorData(item);
					item = { ...item, ...inventoryItem };
					this.updateColorOptionsForTableData(item);

					// Aggregate color upcharges so getItemPrice() includes them in the total.
					// Without this, colorUpCharges is only set when editing via ColorSelectionMenu.
					if (item.colorSelections && item.colorSelections.length > 0) {
						item.colorUpCharges = item.colorSelections.reduce(
							(sum, cs) => sum + (cs.upCharge || 0), 0
						);
					}

					// Restore package context fields (they should not be overwritten by inventory data)
					if (packageCategoryId !== undefined) {
						item.packageCategoryId = packageCategoryId;
					}
					if (subItemCategoryId !== undefined) {
						item.subItemCategoryId = subItemCategoryId;
					}

					item.quantity = item.quantity || 0;

					if (item.lettering) {
						let letterPositionById = this.letterPositions.find(
							(s) => s.letterPositionId == item.lettering.letterPositionId
						);
						item.lettering = { ...item.lettering, ...letterPositionById };
					}

					if (item.children && item.children.length > 0) {
						item.children = await this.processItemsRecursively(item.children);
					}

					item.stockItems = item.stockedItem;

					return item;
				})
			);
		},

		/**
		 * Centralized draft item detection - use this consistently throughout the component
		 * A draft item is one that hasn't been properly selected from inventory yet
		 *
		 * @param {Object} item - Line item to check
		 * @returns {boolean} - True if item is a draft/placeholder
		 */
		isDraftItem(item) {
			if (!item) return false;

			return item.itemId === this.PLACEHOLDER_ITEM_ID ||
			       item.itemId === -1 ||
			       (item.itemId == null && !item.inventoryItemId && !item.styleCode);
		},

		async getQuoteItemSizeColorData(item) {
			try {
				// For draft items (itemId = -1), return empty object since they don't exist in the database yet
				if (this.isDraftItem(item)) {
					return {
						colorOptions: [],
						sizeChart: null,
						type: this.$systemCategories.isSubItem(item.systemCategoryId) ? "SubItem" : "Item"
					};
				}

				// The line item already carries the inventory type from the API; only fall back to the
				// category-based guess when it is absent (e.g. rows built client-side).
				const fallbackType =
					item.type || (this.$systemCategories.isSubItem(item.systemCategoryId) ? "SubItem" : "Item");
				return await fetchItemSizeColorData(this.$apollo, item, fallbackType);
			} catch (error) {
				console.error("[QuoteView] Failed to load color/size data for line item", item.itemId, error);
			}
		},

		updateColorOptionsForTableData(item) {
			// If the item's colorOptions property is empty or undefined, update it
			if (
				(!item.colorOptions || item.colorOptions.length === 0) &&
				item.colorSelections &&
				item.colorSelections.length > 0
			) {
				// Create a colorOption for each colorSelection
				item.colorOptions = item.colorSelections.map((selection, index) => {
					return {
						colorOptionId: index, // Using index as ID. Adjust if needed.
						name: "",
						description: "",
						position: index + 1, // Using index + 1 for position
						colorPalettes: [
							{
								name: "Default",
								description: "Default",
								colors: [
									{
										id: selection.id,
										label: selection.colorName,
										description: selection.colorName,
										hexCode: selection.hexCode,
										isVisible: true,
										colorId: selection.colorId,
										isRetired: false,
										upCharge: selection.upCharge,
										isCustomColor: selection.isCustomColor || false,
									},
								],
							},
						],
					};
				});
			} else if (item.colorOptions && item.colorSelections) {
				// For each colorSelection, check if a corresponding colorOption exists
				item.colorSelections.forEach((selection) => {
					const matchingOption = item.colorOptions.find(
						(option) =>
							option.colorPalettes &&
							option.colorPalettes[0] &&
							option.colorPalettes[0].colors &&
							option.colorPalettes[0].colors.find((color) => color.colorId === selection.colorId)
					);

					// If no matching colorOption is found, add the colorSelection to the first colorOption's colors list
					if (
						!matchingOption &&
						item.colorOptions[0] &&
						item.colorOptions[0].colorPalettes &&
						item.colorOptions[0].colorPalettes[0]
					) {
						const newColor = {
							id: selection.id,
							label: selection.colorName,
							description: selection.colorName,
							hexCode: selection.hexCode,
							isVisible: true,
							colorId: selection.colorId,
							isRetired: false,
							upCharge: selection.upCharge,
							isCustomColor: selection.isCustomColor || false,
						};

						// Add the new color to the first colorOption's colors list
						item.colorOptions[0].colorPalettes[0].colors.push(newColor);
					}
				});
			}

			item.colorUpCharges = item.colorSelections.reduce(
				(acc, s) => acc + (s.upCharge && s.upCharge > 0 ? s.upCharge : 0),
				0
			);
		},

		/**
		 * Recursively apply placeholder ItemId to items and all nested children
		 * that don't have an itemId selected (for draft saves only)
		 */
		applyPlaceholderToItemTree(item, isSubmittingForApproval) {
			const processedItem = { ...item };

			// Apply placeholder if no itemId for draft saves
			if (!isSubmittingForApproval && (item.itemId == null || item.itemId === '' || item.itemId === 0)) {
				processedItem.itemId = this.PLACEHOLDER_ITEM_ID;
			}

			// Recursively process children
			if (item.children && item.children.length > 0) {
				processedItem.children = item.children.map(child =>
					this.applyPlaceholderToItemTree(child, isSubmittingForApproval)
				);
			}

			return processedItem;
		},

		/**
		 * Recursively update local UI data to show DRAFT placeholder styling
		 * for items with itemId === -1
		 */
		updateLocalItemsWithPlaceholderUI(item) {
			// If this item is a placeholder, update its display properties
			if (this.isDraftItem(item)) {
				item.styleCode = '[Draft Item]';
				item.description = '[Placeholder - Item To Be Selected]';

				// Only set systemCategoryId to placeholder if it doesn't have a package/subitem context
				// Package draft items should keep their actual systemCategoryId (e.g., 2 for shells, 3 for skirts)
				if (!item.packageCategoryId && !item.subItemCategoryId) {
					item.systemCategoryId = PLACEHOLDER_SYSTEM_CATEGORY_ID;
				}
			}

			// Recursively update children
			if (item.children && item.children.length > 0) {
				item.children.forEach(child => this.updateLocalItemsWithPlaceholderUI(child));
			}
		},

		/**
		 * Get all DRAFT placeholder items recursively from the items array
		 * Returns array of objects with line number and item reference
		 */
		getDraftItemsList(itemArr, parentLineNumber = '') {
			let draftItems = [];

			itemArr.forEach((item, index) => {
				const lineNumber = parentLineNumber ? `${parentLineNumber}.${index + 1}` : `${index + 1}`;

				// Check if this item is a DRAFT placeholder
				if (item.itemId === this.PLACEHOLDER_ITEM_ID) {
					draftItems.push({
						lineNumber: lineNumber,
						item: item,
						styleCode: item.styleCode || '[Draft Item]',
						description: item.description || '[Placeholder - Item To Be Selected]'
					});
				}

				// Recursively check children
				if (item.children && item.children.length > 0) {
					const childDrafts = this.getDraftItemsList(item.children, lineNumber);
					draftItems = [...draftItems, ...childDrafts];
				}
			});

			return draftItems;
		},

		/**
		 * Mark items with validation errors for visual feedback
		 * Adds hasValidationError property to items that need attention
		 */
		markItemsWithValidationErrors(itemArr, draftItems) {
			// Clear all validation errors first
			this.clearValidationErrors(itemArr);

			// Helper function to recursively mark items
			const markItemRecursively = (item) => {
				if (item.itemId === this.PLACEHOLDER_ITEM_ID) {
					this.$set(item, 'hasValidationError', true);
				}

				// Recursively mark all levels of children
				if (item.children && item.children.length > 0) {
					item.children.forEach(child => {
						markItemRecursively(child);
					});
				}
			};

			// Apply recursive marking to all items
			itemArr.forEach((item) => {
				markItemRecursively(item);
			});
		},

		/**
		 * Clear validation errors from all items
		 */
		clearValidationErrors(itemArr) {
			itemArr.forEach((item) => {
				this.$set(item, 'hasValidationError', false);
				this.$set(item, 'hasColorError', false);
				this.$set(item, 'hasSizeError', false);

				// Recursively clear children
				if (item.children && item.children.length > 0) {
					this.clearValidationErrors(item.children);
				}
			});
		},

		buildLineItemPayload(item) {
			const freeTextAsColorSelections = (item.freeTextSelections || []).map((ft) => ({
				colorOptionId: ft.colorOptionId,
				text: ft.text,
			}));

			const mergedColorSelections = [
				...(item.colorSelections || []),
				...freeTextAsColorSelections,
			];

			return {
				itemId: item.itemId,
				lineItemId: item.lineItemId,
				packageCategoryId: item.packageCategoryId > 0 ? item.packageCategoryId : null,
				subItemCategoryId: item.subItemCategoryId > 0 ? item.subItemCategoryId : null,
				quantity: item.quantity,
				baseQuantity: item.baseQuantity,
				quantityOverride: item.quantityOverride,
				isManuallyOverridden: item.isManuallyOverridden,
				price: item.price,
				upCharge: item.upCharge,
				sizeUpCharge: item.sizeUpCharge,
				dropShip: item.dropShip,
				note: item.note,
				upChargeDescription: item.upChargeDescription,
				includePrice: item.priceIncluded || item.includePrice,
				braidPosition: item.braidPosition,
				colorSelections: mergedColorSelections,
				lettering: this.sanitizeLettering(item.lettering),
				matchPreviousPoNumber: item.matchPreviousPoNumber,
				sizeSelections: (item.sizeSelections || []).map((s) => ({
					...s,
					quantity: s.quantity == "" ? 0 : s.quantity,
				})),
				specialSizeSelections: (item.specialSizeSelections || []).map((s) => ({
					...s,
					quantity: parseInt(s.quantity),
				})),
				children: (item.children || []).map((child) => this.buildLineItemPayload(child)),
			};
		},

		sanitizeLettering(lettering) {
			// Only include lettering if it has valid data
			// letterPositionId must be a positive number (not null/undefined)
			// Otherwise return null to prevent serialization errors
			if (!lettering) return null;

			if (!lettering.letterPositionId || lettering.letterPositionId <= 0) {
				return null;
			}

			// Valid lettering data - return only required fields
			return {
				letterPositionId: lettering.letterPositionId,
				text: lettering.text || ''
			};
		},

		async saveQuote(isSubmittingForApproval = false) {
			this.isSaving = true;
			this.saveError = false;
			this.saveErrors = {};

			const canSave = this.canCreateQuote() || this.canUpdateQuote() || this.canPlaceOrder();
			if (!canSave) {
				this.isSaving = false;
				return;
			}

			// Validate the form
			const isValid = this.validate();

			if (isValid) {
				try {
					// For draft saves, recursively assign placeholder ItemId to items without an item selected
					// This handles items at any nesting depth (parent -> children -> grandchildren -> etc.)
					let itemsToSave = this.quoteData.items.map((item) =>
						this.applyPlaceholderToItemTree(item, isSubmittingForApproval)
					);

					let salesQuote = {
						lineItems: itemsToSave.map((item) => this.buildLineItemPayload(item)),
						customerId: this.quoteData.customerId,
						contactId: this.quoteData.contactId,
						shippingMethodId: this.quoteData.shippingMethodId,
						paymentTermsId: this.quoteData.paymentTermsId,
						billToId: this.quoteData.billToId,
						shipToId: this.quoteData.shipToId,
						salesRepId: this.quoteData.salesRepId,
						expectedOrderDate: this.quoteData.expectedOrderDate || null,
						attribution: this.quoteData.attribution,
						schoolPo: this.quoteData.schoolPo,
						referenceNumber: this.quoteData.referenceNumber,
						shippingAndHandling: this.shippingAndHandling,
						shippingAndHandlingDiscount: this.shippingAndHandlingDiscount,
						shippingAndHandlingManualOverride: this.shippingAndHandlingManualOverride,
						tax: this.computedTax,
						taxRate: this.computedTaxRate,
						taxRateManualOverride: this.taxRateManualOverride,
						total: this.computedOrdertotal,
						balance: this.computedBalance,
					};

					let res;
					if (this.quoteData.quoteId) {
						res = await this.$http.put(`/api/quotes/${this.quoteData.quoteId}`, {
							...salesQuote,
							quoteId: this.quoteData.quoteId,
						});
						//save notes on quote
						this.saveQuoteNotes(this.quoteData.quoteId);
					} else {
						res = await this.$http.post("/api/quotes", salesQuote);

						if (res.status !== 200) {
							this.isSaving = false;
							this.saveError = true;
							this.saveErrors = res.data.errors;
							this.$toast?.error('Quote failed to save! Please check all required fields.');
							return;
						}

						//save payments and notes on quote
						if (!this._paymentsSavedSeparately) {
							this.saveQuotePayments(res.data.quoteId);
						}
						this._paymentsSavedSeparately = false;
						this.saveQuoteNotes(res.data.quoteId);

						//assign quoteNumber for newly created quote
						this.quoteData.quoteNumber = res.data.quoteNumber;
						this.quoteData.quoteId = res.data.quoteId;

						// Reload line items to get server-assigned LineItemIds
						// Without this, subsequent saves send lineItemId: null and the
						// backend can't match items for merge or audit diffing
						await this.getQuoteItems(res.data.quoteId);
					}
					console.log(salesQuote, res);

					// Both save endpoints return the derived status (Incomplete / Final Check, #1498).
					// Apply it before the unsaved-changes snapshot is reset below: status is part of
					// getTrackableState(), so setting it afterwards would trip the guard.
					if (res?.data?.status) {
						this.quoteData.status = quoteStatusKey(res.data.status);
					}

					// Update local items to apply placeholder ItemIds from the save operation
					if (!isSubmittingForApproval) {
						this.quoteData.items = this.quoteData.items.map((item) =>
							this.applyPlaceholderToItemTree(item, isSubmittingForApproval)
						);

						// Update local UI data to show DRAFT placeholder styling
						this.quoteData.items.forEach(item => this.updateLocalItemsWithPlaceholderUI(item));
					}

					//display save confirmation
					this.isSaving = false;
					this.$toast?.success('Quote saved successfully!');

					// Disable navigation warning (user explicitly saved)
					this.showNavigationWarning = false;

					// Reset initial state after successful save
					this.$nextTick(() => {
						this.resetInitialState();
					});

					if (this.$route.path.endsWith("/new") && !this.savingBeforeLeave) {
						this.$router.push(`/sales-orders/quotes/${this.quoteData.quoteId}`);
					}

					this.canUpdate = await this.canUpdateQuote();
				} catch (error) {
					console.log(error.message);

					this.isSaving = false;
					this.saveError = true;
					const serverMessage = error?.response?.data?.message || error?.response?.data?.error;
					this.$toast?.error(serverMessage || 'Quote failed to save! Please check all required fields.', { timeout: serverMessage ? 10000 : 5000 });
					if (serverMessage && /can no longer be edited|cannot be edited/.test(serverMessage) && this.quoteData.quoteId) {
						// The quote was locked since this tab loaded it; show the locked state
						await this.getQuoteInfo(this.quoteData.quoteId);
						this.$nextTick(() => this.resetInitialState());
					}
					// this.saveErrors = res.data.errors
				}
			}
		},

		async saveQuotePayments(quoteId) {
			try {
				let payments = this.quoteData.payments.map((payment) => {
					let paymentData = {
						...payment,
						// Map GraphQL salesPaymentId → REST API paymentId
						paymentId: payment.paymentId || payment.salesPaymentId || 0,
						quoteId: quoteId,
					};

					return paymentData;
				});

				const res = await this.$http.put(`/api/quotes/${quoteId}/payments`, payments);

				// Success — update local payment IDs with DB-assigned values
				if (res.status === 200 && Array.isArray(res.data)) {
					res.data.forEach((saved, i) => {
						if (i < this.quoteData.payments.length) {
							this.quoteData.payments[i].paymentId = saved.paymentId;
						}
					});
					this.$toast?.success("Payments saved successfully");
					return true;
				}
				return false;
			} catch (error) {
				console.error("Payment save error:", error);

				// Extract user-friendly error message
				let errorMessage = "Failed to save payments. Please check all payment fields.";

				if (error.response?.data?.errors) {
					// ASP.NET validation errors - filter out technical JSON messages
					const errors = Object.values(error.response.data.errors)
						.flat()
						.filter(msg => !msg.includes("JSON") && !msg.includes("BytePositionInLine"));

					if (errors.length > 0) {
						errorMessage = errors.join(". ");
					}
				} else if (error.response?.data?.message) {
					// Custom error message
					errorMessage = error.response.data.message;
				}

				// Show user-facing error notification
				this.$toast?.error(errorMessage);
				return false;
			}
		},

		async saveQuoteNotes(quoteId) {
			try {
				let notes = this.quoteData.notes.map((note) => {
					let noteData = {
						...note,
						quoteId: quoteId,
					};

					return noteData;
				});

				const res = await this.$http.put(`/api/quotes/${quoteId}/notes`, notes);
				console.log(res);
			} catch (error) {
				console.log(error);
			}
		},

		async placeOrder(quoteId) {
			if (this.saveError) {
				return;
			}
			try {
				let request = {
					quoteId: quoteId,
				};
				const res = await this.$http.post(`/api/orders`, request);
				console.log(res);

				// Check response status
				if (res.status === 200) {
					this.$toast?.success('Order placed successfully!');

					// Note: User remains on quote page - jumplink to order is already available in the UI
				}
			} catch (error) {
				console.error('Error placing order:', error);

				// Extract error message from response
				let errorMessage = 'Failed to submit quote for approval. ';

				if (error.response) {
					// Backend returned an error response
					if (error.response.data) {
						// Check for common error formats
						if (typeof error.response.data === 'string') {
							errorMessage += error.response.data;
						} else if (error.response.data.error) {
							errorMessage += error.response.data.error;
						} else if (error.response.data.message) {
							errorMessage += error.response.data.message;
						} else if (error.response.data.title) {
							// ASP.NET Core ProblemDetails format
							errorMessage += error.response.data.title;
							if (error.response.data.detail) {
								errorMessage += ': ' + error.response.data.detail;
							}
						} else {
							errorMessage += 'Please check all line items and try again.';
						}
					} else {
						errorMessage += `Server error: ${error.response.status} ${error.response.statusText}`;
					}
				} else if (error.request) {
					// Request was made but no response received
					errorMessage += 'No response from server. Please check your connection.';
				} else {
					// Something else happened
					errorMessage += error.message || 'An unexpected error occurred.';
				}

				// Display error to user
				this.$toast?.error(errorMessage, {
					timeout: 10000, // Show for 10 seconds
				});

				// Set error state to prevent navigation
				this.saveError = true;
			}
		},

		async submitForApproval() {
			// First validate the quote (reuse existing validation)
			if (!this.validateQuoteForm(true)) return;

			this.isSubmitting = true;
			try {
				// Save any pending changes first
				await this.saveQuote();

				const response = await this.$http.post(`/api/quotes/${this.quoteData.quoteId}/submit`);
				this.$toast.success('Quote submitted for approval');
				this.$router.push('/sales-orders/pending-approvals');
			} catch (error) {
				this.$toast.error(error.response?.data?.message || 'Failed to submit quote');
				// A refused submit can return the quote to Incomplete (#1498); reload so the
				// badge shows it, then re-snapshot so the unsaved-changes guard does not fire.
				if (this.quoteData.quoteId) {
					await this.getQuoteInfo(this.quoteData.quoteId);
					this.$nextTick(() => {
						this.resetInitialState();
					});
				}
			} finally {
				this.isSubmitting = false;
			}
		},

		async approveQuote() {
			this.isApproving = true;
			try {
				const response = await this.$http.post(`/api/quotes/${this.quoteData.quoteId}/approve`);
				this.$toast.success('Quote approved! Order created.');
				// Redirect to sample orders page if this is a sample order
				this.$router.push(`${this.orderBasePath}/${response.data.orderId}`);
			} catch (error) {
				this.$toast.error(error.response?.data?.message || 'Failed to approve quote');
			} finally {
				this.isApproving = false;
			}
		},

		async revertToQuote() {
			this.isReverting = true;
			try {
				await this.$http.post(`/api/quotes/${this.quoteData.quoteId}/revert`);
				this.$toast.success('Quote reverted to incomplete status');
				// Refresh the quote data to show updated status
				await this.getQuoteInfo(this.quoteData.quoteId);
			} catch (error) {
				this.$toast.error(error.response?.data?.message || 'Failed to revert quote');
			} finally {
				this.isReverting = false;
			}
		},

		handleClose() {
			if (this.pendingApprovalMode) {
				this.$router.push('/sales-orders/pending-approvals');
			} else {
				this.$router.push('/sales-orders/quotes');
			}
		},

		validateQuoteForm(forSubmission) {
			this.shouldFullyValidate = forSubmission;
			this.itemDataIncomplete = forSubmission;

			// Check for validation errors in the form sections
			const isFormSectionClear = this.validate();

			// Check that lineitems exist and pass requirements
			if (this.quoteData.items.length == 0 && forSubmission) {
				this.itemDataIncomplete = true;
				this.$toast?.error('Cannot submit quote for approval. At least one item is required.', {
					timeout: 8000,
				});
				return false;
			}

			if (forSubmission) {
				// Check for DRAFT placeholder items
				const draftItems = this.getDraftItemsList(this.quoteData.items);
				if (draftItems.length > 0) {
					// Mark draft items with validation errors for visual feedback
					this.markItemsWithValidationErrors(this.quoteData.items, draftItems);

					this.$toast?.error('Cannot submit quote for approval. Draft Items need to be selected or removed.', {
						timeout: 8000,
					});
					this.itemDataIncomplete = true;
					return false;
				}

				// Check existing validations (colors, sizes)
				const incompleteItems = this.validateQuoteItems(this.quoteData.items);
				if (incompleteItems.length > 0) {
					this.itemDataIncomplete = true;
					this.$toast?.error('Cannot submit quote for approval. Some items have missing color or size selections.', {
						timeout: 8000,
					});
					return false;
				} else {
					this.itemDataIncomplete = false;
				}
			}

			return isFormSectionClear && !this.itemDataIncomplete;
		},

		validate() {
			this.saveError = false;
			this.errors = {
				quoteData: {
					customerId: false,
					contactId: false,
					shippingMethodId: false,
					paymentTermsId: false,
					billToId: false,
					shipToId: false,
					salesRepId: false,
					expectedOrderDate: false,
				},
				shippingAndHandling: false,
				shippingAndHandlingDiscount: false,
			};

			if (this.shouldFullyValidate) {
				if (
					this.quoteData.customerId === null ||
					this.quoteData.contactId === null ||
					this.quoteData.shippingMethodId === null ||
					this.quoteData.shipToId === null ||
					this.quoteData.billToId === null ||
					this.quoteData.paymentTermsId === null ||
					this.quoteData.salesRepId === null
				) {
					this.isSaving = false;
					this.saveError = true;

					if (this.quoteData.customerId === null) {
						this.saveErrors = { ...this.saveErrors, customerId: "" };
						this.errors.quoteData.customerId = true;
					}
					if (this.quoteData.contactId === null) {
						this.saveErrors.contactId = "";
						this.errors.quoteData.contactId = true;
					}
					if (this.quoteData.shippingMethodId === null) {
						this.saveErrors.shippingMethodId = "";
						this.errors.quoteData.shippingMethodId = true;
					}
					if (this.quoteData.shipToId === null) {
						this.saveErrors.shipToId = "";
						this.errors.quoteData.shipToId = true;
					}
					if (this.quoteData.billToId === null) {
						this.saveErrors.billToId = "";
						this.errors.quoteData.billToId = true;
					}
					if (this.quoteData.paymentTermsId === null) {
						this.saveErrors.paymentTermsId = "";
						this.errors.quoteData.paymentTermsId = true;
					}
					if (this.quoteData.salesRepId === null) {
						this.saveErrors.salesRepId = "";
						this.errors.quoteData.salesRepId = true;
					}

					return false;
				}
			}

			if (
				this.quoteData.customerId === null ||
				this.quoteData.contactId === null ||
				this.quoteData.salesRepId === null
			) {
				this.isSaving = false;
				this.saveError = true;

				if (this.quoteData.customerId === null) {
					this.saveErrors = { ...this.saveErrors, customerId: "" };
					this.errors.quoteData.customerId = true;
				}
				if (this.quoteData.contactId === null) {
					this.saveErrors.contactId = "";
					this.errors.quoteData.contactId = true;
				}
				if (this.quoteData.salesRepId === null) {
					this.saveErrors.salesRepId = "";
					this.errors.quoteData.salesRepId = true;
				}

				return false;
			}

			return true;
		},

		async validateQuote(processQuote = true) {
			this.shouldFullyValidate = processQuote;
			this.itemDataIncomplete = processQuote;
			//check for validation errors in the form sections
			const isFormSectionClear = this.validate();

			//check that lineitems exist and pass requirments
			// ONLY validate line items when submitting for approval (processQuote = true)
			// Allow incomplete/empty line items for draft saves
			if (this.quoteData.items.length == 0 && processQuote) {
				this.itemDataIncomplete = true;
			} else if (processQuote) {
				// Check for DRAFT placeholder items when submitting for approval
				const draftItems = this.getDraftItemsList(this.quoteData.items);
				if (draftItems.length > 0) {
					// Mark draft items with validation errors for visual feedback
					this.markItemsWithValidationErrors(this.quoteData.items, draftItems);

					// Show simplified error message
					this.$toast?.error('Cannot submit quote for approval. Draft Items need to be selected or removed.', {
						timeout: 8000,
					});

					this.itemDataIncomplete = true;
					return;
				}

				// Check existing validations (colors, sizes)
				const incompleteItems = this.validateQuoteItems(this.quoteData.items);
				if (incompleteItems.length > 0) {
					this.itemDataIncomplete = true;
					this.$toast?.error('Cannot submit quote for approval. Some items have missing color or size selections.', {
						timeout: 8000,
					});
				} else {
					this.itemDataIncomplete = false;
				}
			} else {
				// For draft saves, don't validate line items but clear any existing validation errors
				this.itemDataIncomplete = false;
				this.clearValidationErrors(this.quoteData.items);
			}

			if (!isFormSectionClear || this.itemDataIncomplete) return;

			await this.saveQuote(processQuote);

			if (this.quoteData.quoteId && processQuote) {
				await this.placeOrder(this.quoteData.quoteId);
				await this.getQuoteInfo(this.quoteData.quoteId);
				await this.applyQuoteMeta();
			}
		},

		validateQuoteItems(itemArr) {
			let itemsIncomplete = [];

			//recursively loop through all passed in items and check if color options and size options requirments are met
			itemArr.forEach((item) => {
				let hasColorError = false;
				let hasSizeError = false;

				//color option validations
				if (
					!hasValidColorSelections(
						item,
						this.$store.getters.requiresLettering(item.systemCategoryId) && this.shouldFullyValidate
					)
				) {
					hasColorError = true;
				}

				//sizing validations
				if (item.sizeChart && item.sizeChart.sizes.length > 0) {
					let total = 0;
					let quantites = [];

					//loop through sizeData and grab each quantity array
					// sizeSelections is null (not []) when the item's quantity is covered
					// entirely by special sizes, so fall back to an empty array (#1424)
					(item.sizeSelections || []).forEach((obj) => {
						if (obj.quantity) {
							quantites = [...quantites, obj.quantity];
						}
					});

					//loop through special sizing and grab each qauntity
					if (item.specialSizeSelections && item.specialSizeSelections.length > 0) {
						item.specialSizeSelections.forEach((obj) => {
							if (obj.quantity !== null) {
								quantites.push(obj.quantity);
							}
						});
					}

					//calculate total quantites
					total = quantites
						.filter((i) => i !== "")
						.reduce((a, b) => parseInt(a) + parseInt(b), 0);

					if (total !== parseInt(item.quantity)) {
						hasSizeError = true;
					}
				}

				// If item has validation errors, mark it and add to incomplete list
				if (hasColorError || hasSizeError) {
					this.$set(item, 'hasColorError', hasColorError);
					this.$set(item, 'hasSizeError', hasSizeError);
					itemsIncomplete.push(item);
				} else {
					// Clear error flags if validation passes
					this.$set(item, 'hasColorError', false);
					this.$set(item, 'hasSizeError', false);
				}

				//if items have children check them for requirments
				if (item.children && item.children.length > 0) {
					itemsIncomplete = [...itemsIncomplete, ...this.validateQuoteItems(item.children)];
				}
			});

			return itemsIncomplete;
		},

		async getTaxRate(zipCode, state) {
			// Sample orders default to a 0% tax rate unless manually overridden (#1388)
			if (this.hasSampleItems && !this.taxRateManualOverride) {
				this.computedTaxRate = 0;
				return;
			}
			if (zipCode && state) {
				const { data } = await this.$apollo.query({
					query: GET_TAX_RATE,
					variables: {
						zipCode,
						state,
					},
				});

				let combinedRate = data.zipCodeTaxRates?.items?.[0]?.combinedRate;
				if (combinedRate) {
					this.computedTaxRate = combinedRate || 0;
				} else {
					// Check cache first
					if (this.taxRateCache[zipCode]) {
						this.computedTaxRate = this.taxRateCache[zipCode];
					} else {
						try {
							const apiUrl = `https://api.api-ninjas.com/v1/salestax?zip_code=${zipCode}`;
							const response = await new Promise((resolve, reject) => {
								request.get(
									{
										url: apiUrl,
										headers: { "X-Api-Key": process.env.VUE_APP_NINJAS_KEY },
									},
									(error, response, body) => {
										if (error) {
											reject(error);
										} else if (response.statusCode != 200) {
											reject(`Error: ${response.statusCode}, ${body}`);
										} else {
											resolve(JSON.parse(body));
										}
									}
								);
							});

							if (response && response.length > 0) {
								const taxData = response[0];
								const taxRate = parseFloat(taxData.total_rate) * 100 || 0;
								this.computedTaxRate = taxRate;
								this.taxRateCache[zipCode] = taxRate; // Cache the result
							} else {
								this.computedTaxRate = 0;
							}
						} catch (error) {
							console.error("Error fetching tax rate:", error);
							this.computedTaxRate = 0;
						}
					}
				}
			} else {
				this.computedTaxRate = 0;
			}
		},

		handleSave(resolve, reject, existing) {
			this.selectedLineItem = {
				upCharge: existing && existing.upCharge ? existing.upCharge : null,
				upChargeDescription:
					existing && existing.upChargeDescription ? existing.upChargeDescription : "",
			};
			this.showModal = true;
			this.resolve = resolve;
			this.reject = reject;
		},

		confirmAction() {
			if (this.resolve) {
				this.resolve({
					upChargeDescription: this.selectedLineItem.upChargeDescription,
					upCharge: this.selectedLineItem.upCharge,
				});
				this.showModal = false;
			}
		},

		cancelAction() {
			if (this.reject) {
				this.reject(new Error("Operation cancelled by user."));
				this.showModal = false;
			}
		},

		itemText(item) {
			return item.customer && item.customer.organization
				? `${item.firstName} ${item.lastName} (${item.customer.organization})`
				: `${item.firstName} ${item.lastName}`;
		},

		async handleCancelQuote(isCancelled) {
			let quoteNumber = this.quoteData.quoteNumber;
			const res = await this.$http.post(
				isCancelled ? `/api/quotes/${quoteNumber}/cancel` : `/api/quotes/${quoteNumber}/restore`
			);
			console.log(res);
			await this.init();
		},

		async initWithCustomerRetry(customerId) {
			let retries = 0;
			while (retries < this.maxRetries) {
				try {
					await this.getCustomerInfo(customerId);
					// If getCustomerInfo succeeds, exit the retry loop
					return;
				} catch (error) {
					console.log(`Error initializing with customer (attempt ${retries + 1}):`, error);
					retries++;
					if (retries >= this.maxRetries) {
						console.error("Max retries reached. Unable to initialize with customer.");
					} else {
						await new Promise((resolve) => setTimeout(resolve, this.retryDelay));
					}
				}
			}
		},

		handleResize() {
			// Force recomputation of tableBodyHeight
			this.calculateTableHeight();
		},

		calculateTableHeight() {
			// Only calculate if we're in the primary view
			if (!this.primaryView) return;

			this.computedTableHeight = 250;
			this.$nextTick(() => {
				// Get the window height
				const windowHeight = window.innerHeight;

				// Get container heights
				const detailContainer = document.getElementById("detail-container")?.offsetHeight || 0;
				const footerContainer =
					document.getElementById("sales-footer-container")?.offsetHeight || 0;

				// Calculate available space (subtracting padding and margins)
				const availableHeight = windowHeight - detailContainer - footerContainer - 64 - 36; // 64px for header, 36px for padding

				this.computedTableHeight = Math.max(availableHeight, 200);
			});
		},

		async getUserName(id) {
			try {
				const response = await this.$apollo.query({
					query: gql`
						query GetUserName($userId: Int!) {
							userNameById(userId: $userId) {
								fullName
								username
							}
						}
					`,
					variables: {
						userId: id,
					},
					fetchPolicy: "no-cache",
				});

				const user = response.data.userNameById;
				return user?.fullName || user?.username || "...";
			} catch (error) {
				console.error("Error fetching user:", error);
				return "...";
			}
		},

		async init() {
			//check if quote prop with id is passed in and retrieve that quotes data
			if (this.quote?.quoteId) {
				await this.getQuoteInfo(this.quote.quoteId);
				this.applyQuoteMeta();
			}

			// Set what permissions are available to this user.
			if (this.canUpdateQuote()) {
				this.canUpdate = true;
			} else {
				this.readonly = this.quoteData.quoteId ? true : false;
			}

			this.canCreate = this.canCreateQuote();

			if (this.initWithCustomer) this.initWithCustomerRetry(this.initWithCustomer);

			this.calculateTableHeight();

			await this.$nextTick(() => {
				this.isLoaded = true;
			});

			// For new quotes, delay captureInitialState to allow SalesEntryTable's
			// default empty row (added on mount via $nextTick) to propagate back
			// through v-model before we snapshot the initial state. Without this,
			// the initial state captures items=[] while the current state has the
			// auto-added empty row, causing a false-positive unsaved changes warning.
			if (!this.quote?.quoteId) {
				await this.$nextTick();
				await this.$nextTick();
				this.captureInitialState();
			}
		},
	},
	async mounted() {
		if (this.$route.query.clonedFromOrder) {
			const clonedQuote = this.$store.getters["clonedQuote"];
			if (clonedQuote) {
				this.quoteData = {
					...this.quoteData,
					...clonedQuote,
				};
				this.$store.dispatch("clearClonedQuote");
			}
		}
		await this.init();
		await this.$nextTick();
	},
	beforeDestroy() {
		// Clean up timeouts to prevent memory leaks
		if (this.customerSearchTimeout) {
			clearTimeout(this.customerSearchTimeout);
		}
		if (this.contactSearchTimeout) {
			clearTimeout(this.contactSearchTimeout);
		}
	},
};
</script>

<style lang="scss" scoped>
.quote-locked-banner {
	font-size: 1.4rem;
}

.quote {
	&-actions {
		display: flex;
		width: 100%;
		justify-content: flex-end;
		align-items: center;
		gap: 0.8rem;

		.v-btn {
			background-color: $color-white;
			border-color: $color-gray;
			text-transform: initial;

			&.kebab-btn {
				min-width: 0;
				padding: 0 0.8rem;
			}
		}
	}

	&-form {
		position: relative;
		display: block;
		background-color: $color-white;
		border: 1px solid $color-gray;
		border-radius: 3px;
		padding: 0.5rem 1.5rem;

		.row {
			margin: initial;
		}

		.form-input {
			margin: initial;
		}

		&-header {
			display: flex;
			align-items: center;

			a {
				color: $color-tertiary;
			}
		}

		&-title {
			display: inline-block;
			font-size: 2.2rem;
			font-weight: 700;
			padding-right: 1rem;
		}
		&-status {
			text-transform: uppercase; // statuses read as labels everywhere (#300)
			display: inline-block;
			border: 1px solid;
			font-size: 1.4rem;
			padding: 0.5rem 1rem;
			border: 1px solid;

			&-orange {
				border-color: $color-orange;
				color: $color-orange;
			}

			&-green {
				border-color: $color-green;
				color: $color-green;
			}

			&-red {
				border-color: red;
				color: red;
			}
		}

		&-divider {
			display: block;
			width: calc(100% - 24px);
			height: 1px;
			margin: 1.2rem auto;
			background-color: $color-gray;
		}
	}

	&-notes {
		position: relative;
		display: inline-flex;
		flex-flow: column;
		width: 60%;
		height: 100%;
		border: 1px solid $color-gray;
		background-color: $color-white;

		&-heading {
			display: flex;
			flex-flow: row nowrap;
			align-items: center;
			width: 100%;
			height: 4.7rem;
			background-color: $color-gray-light-2;
			border-bottom: 1px solid $color-gray;
			padding: 0.5rem 1rem;
			z-index: 1;

			h2 {
				font-size: 1.4rem;
				font-weight: 700;
				text-transform: uppercase;
				margin-right: auto;
			}

			.v-btn {
				background-color: $color-white;

				&:nth-child(3) {
					margin-left: 0.8rem;
				}
			}
		}

		&-container {
			position: absolute;
			padding-top: 4.7rem; //match with heading height
			top: 0;
			bottom: 0;
			left: 0;
			right: 0;
			flex-grow: 1;
			background-color: $color-white;
			overflow-y: auto;

			&-list {
				display: flex;
				flex-flow: column nowrap;
				width: 100%;
			}

			&-note {
				display: flex;
				flex-flow: row nowrap;
				align-items: center;
				padding: 1rem 0.8rem;
				width: 100%;
				font-size: 1.4rem;
				border-bottom: 1px solid $color-gray;

				p {
					flex: 1 1 auto;
				}

				&-type {
					padding: 0.5rem 1rem;
					min-width: 12rem;
					text-align: center;
					border-radius: 25rem;
					background-color: $color-gray;
					font-weight: 700;
					font-size: 1.4rem;
					margin-right: 1rem;

					&-2 {
						background-color: $color-green;
					}
				}

				&-author {
					margin-left: 1rem;
				}
			}
		}

		&-dropdown {
			position: relative;
			display: flex;
			flex-flow: column;
			background-color: $color-white;
			height: 0%;
			border-bottom: 1px solid transparent;
			overflow: hidden;
			transition: height 0.3s ease-in-out;
			z-index: 5;

			&-active {
				height: 100%;
				box-shadow: rgba(50, 50, 93, 0.05) 0px 2px 5px -1px, rgba(0, 0, 0, 0.2) 0px 1px 3px -1px !important;
			}

			.form-input,
			.form-textarea {
				margin: 1.25rem;
			}

			.form-input {
				display: inline-flex;
				width: calc(50% - 2.5rem);
			}

			.form-textarea {
				label {
					width: 19.5%;
				}
			}

			&-author {
				display: flex;
				align-items: center;
				justify-content: flex-end;
				font-size: 1.4rem;
				margin: 1.25rem;
				margin-top: auto;

				h3 {
					font-weight: normal;
					padding-right: 0.5rem;
				}

				p {
					font-size: 1.6rem;
				}
			}
		}
	}

	&-pricing {
		display: inline-flex;
		flex-flow: column nowrap;
		width: 40%;
		text-align: right;

		.form-input {
			flex-flow: row;
			align-items: center;
			margin: 0.4rem 0;

			label {
				font-weight: 700;
				margin-right: 1rem;
			}
			.v-text-field {
				min-width: 16rem;
				text-align: right;

				&::v-deep {
					input {
						text-align: right;
						padding-right: 0.8rem;
					}
				}
			}
		}
	}

	.col {
		padding: 5px !important;
	}
}

.v-input__control {
	display: flex;
	flex-direction: column;
	flex-wrap: nowrap !important;

	.v-text-field__details {
		padding: 0px !important;
	}
}
</style>

<style lang="scss">
.to-order {
	cursor: pointer;

	&:hover {
		color: #1264f6;
	}
}

::v-deep .text--disabled {
	opacity: 0.6;
}

.manual-override {
	::v-deep .v-input__slot {
		background-color: #fff3cd !important;
	}
}

.tax-rate-edit-icon {
	padding-left: 0.5rem;
	cursor: pointer;
	margin-right: 4px;
	&:hover {
		color: #1976d2 !important;
	}
}

.tax-rate-input.number-input .v-text-field__slot::before,
.tax-rate-input.number-input .v-text-field__slot::after {
	display: none !important;
}

.quote-overflow-menu.v-menu__content {
	min-width: 200px !important;
	.v-list-item {
		min-height: 44px !important;
		padding: 0 20px !important;
	}
	.v-list-item__title {
		font-size: 1.4rem !important;
	}
	.v-list-item__icon {
		margin-right: 12px !important;
	}
}
</style>

<style lang="scss" scoped>
.v-overlay {
	::v-deep {
		.v-overlay__content {
			width: 95%;
		}
	}

	&-relative {
		position: relative;
		padding: 3rem 0;
		width: 100%;
	}
}

.warning-modal {
	max-width: 55rem;

	&-title {
		font-weight: 700;
	}

	&-message {
		font-size: 1.6rem;
		font-weight: 700;
		text-align: center;
	}

	&-list {
		list-style: none;
		text-align: center;
		padding-left: 0;
		margin-top: 1rem;

		li {
			margin: 1rem 1.75rem;
		}
	}
}
</style>
